import React, { useState, useEffect, useCallback } from "react";
import {
  Tabs,
  Row,
  Col,
  Button,
  Divider,
  Tag,
  Table,
  Typography,
  Input,
  message,
  Form,
  Space,
  Upload,
  Modal,
  Dropdown,
  Checkbox,
  InputNumber,
  Select,
  Card,
  Radio,
  Alert,
  Descriptions,
  DatePicker,
  Tooltip,
  Badge,
  Collapse,
} from "antd";
import {
  EditOutlined,
  DownloadOutlined,
  SaveOutlined,
  UndoOutlined,
  UploadOutlined,
  FileOutlined,
  DeleteOutlined,
  EyeOutlined,
  DownOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  ReloadOutlined,
  PlusOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  ExportOutlined,
  CopyOutlined,
  LinkOutlined,
} from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import type { UploadFile, UploadProps } from "antd/es/upload/interface";
import { downloadFile } from "../../utils/downloadFile";
import { supabase } from "../../lib/supabase";
import { generateAndSaveShippingLabel } from "../../utils/shippingLabel";
import {
  Booking,
  Product,
  bookingStatusLabels,
  BookingStatus,
  Agent,
  TransportType,
  Container,
  Supplier,
} from "../../types/booking";
import {
  getStatusColor,
  formatDate,
  formatDateTime,
} from "../../utils/bookingUtils";
import {
  updateBookingDetails,
  updateBookingCommercialInvoice,
  updateBookingProduct,
  deleteBookingProduct,
  updateBookingStatus,
  fetchAgents,
  updateBookingAgent,
  updateDeliveryInfo,
  createDeliveryInfo,
  deleteDeliveryInfo,
  updateBookingSupplier,
  linkBookingToContainer,
} from "../../services/bookingService";
import { trackFeatureUsage } from "../../lib/posthog";
import {
  uploadCommercialInvoice,
  downloadCommercialInvoice,
  createCommercialInvoiceSignedUrl,
} from "../../utils/commercialInvoiceUtils";
import {
  uploadBookingInvoice,
  downloadBookingInvoice,
  createBookingInvoiceSignedUrl,
} from "../../utils/bookingInvoiceUtils";
import {
  uploadOtherDocument,
  downloadOtherDocument,
  createOtherDocumentSignedUrl,
} from "../../utils/otherDocumentsUtils";
import { PicqerService } from "../../services/picqerService";
import PurchaseOrderSection from "./PurchaseOrderSection";
import {
  createBookingInvoice,
  getBookingInvoices,
  deleteBookingInvoice,
} from "../../services/bookingInvoiceService";
import {
  createOtherDocument,
  getOtherDocuments,
  deleteOtherDocument,
} from "../../services/otherDocumentsService";
import dayjs from "dayjs";
import { useTransportTypes } from "../../hooks/useTransportTypes";
import { useBookingTypes } from "../../hooks/useBookingTypes";
import { useIncoterms } from "../../hooks/useIncoterms";
import { fetchContainers } from "../../services/containerService";
import { useNavigate } from "react-router-dom";
import { getCompanyUsers } from "../../services/adminUsers";
import { ShippingOptions } from "../sendcloud/ShippingOptions";
import { SendcloudShipments } from "../sendcloud/SendcloudShipments";
import * as XLSX from "xlsx";
import { InvoiceForm } from "../invoices";
import { BookingProductImport } from "../invoices/form-sections/BookingProductImport";
import { Invoice } from "../../types/invoice";

const { TabPane } = Tabs;
const { Title, Text } = Typography;
const { TextArea } = Input;
const { Dragger } = Upload;

// Modal width for booking details
const MODAL_WIDTH = 1200;

interface BookingDetailsProps {
  booking: Booking;
  onEdit: (booking: Booking) => void;
  onDownloadPackingSlip: (booking: Booking) => void;
  onRefresh: () => void;
  onChangesMade?: (hasChanges: boolean) => void;
  defaultTab?: string;
}

interface DeliveryInfo {
  id: number;
  street_name?: string;
  house_number?: string;
  city?: string;
  postal_code?: string;
  country?: string;
  instructions?: string;
}

// Task interface
interface Task {
  id: number;
  created_at: string;
  booking_id: number | null;
  company_id: number | null;
  date: string | null;
  name: string | null;
  description: string | null;
  completed: boolean | null;
  team_member: number | null;
  completed_on: string | null;
  team_members?: {
    id: number;
    name: string;
  };
}

const BookingDetails: React.FC<BookingDetailsProps> = ({
  booking,
  onEdit,
  onDownloadPackingSlip,
  onRefresh,
  onChangesMade,
  defaultTab = "1",
}) => {
  // Local state declarations - all must come before any conditionals
  const [editMode, setEditMode] = useState(false);
  const [editTarget, setEditTarget] = useState<
    "general" | "supplier" | "delivery" | null
  >(null);
  const [saving, setSaving] = useState(false);
  const [approvingBooking, setApprovingBooking] = useState(false);
  const [rejectingBooking, setRejectingBooking] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState("");
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [approvalNotes, setApprovalNotes] = useState("");
  const [uploadingInvoice, setUploadingInvoice] = useState(false);
  const [invoiceFile, setInvoiceFile] = useState<File | null>(null);
  const [invoicePreviewVisible, setInvoicePreviewVisible] = useState(false);
  const [invoicePreviewUrl, setInvoicePreviewUrl] = useState("");
  const [appendInvoice, setAppendInvoice] = useState(true);
  const [form] = Form.useForm();
  const [productForm] = Form.useForm();
  const [supplierForm] = Form.useForm();
  const [isEditingSupplier, setIsEditingSupplier] = useState(false);
  const [savingSupplier, setSavingSupplier] = useState(false);
  const [bookingInvoiceFile, setBookingInvoiceFile] = useState<File | null>(
    null
  );
  const [uploadingBookingInvoice, setUploadingBookingInvoice] = useState(false);
  const [bookingInvoices, setBookingInvoices] = useState<
    Array<{
      id: number;
      booking_invoice_file: string;
      created_at: string;
    }>
  >([]);
  const [bookingInvoiceDescription, setBookingInvoiceDescription] =
    useState("");
  const [bookingInvoicePreviewVisible, setBookingInvoicePreviewVisible] =
    useState(false);
  const [bookingInvoicePreviewUrl, setBookingInvoicePreviewUrl] = useState("");
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState<number | null>(null);
  const [deletingInvoice, setDeletingInvoice] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [isProductModalVisible, setIsProductModalVisible] = useState(false);
  const [savingProduct, setSavingProduct] = useState(false);
  const [agents, setAgents] = useState<Agent[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [updatingAgent, setUpdatingAgent] = useState(false);
  const [selectedAgentId, setSelectedAgentId] = useState<number | null>(
    booking?.agent_id || null
  );
  const [isEditingDelivery, setIsEditingDelivery] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [editingDeliveryId, setEditingDeliveryId] = useState<number | null>(
    null
  );
  const [processingDelivery, setProcessingDelivery] = useState(false);
  const [deliveryType, setDeliveryType] = useState<"address" | "storage">(
    booking.booking_delivery_info ? "address" : "storage"
  );
  // Tasks states
  const [tasks, setTasks] = useState<any[]>([]);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [isTaskModalVisible, setIsTaskModalVisible] = useState(false);
  const [taskForm] = Form.useForm();
  const [memberOptions, setMemberOptions] = useState<
    Array<{ label: string; value: number }>
  >([]);

  // Container states
  const [availableContainers, setAvailableContainers] = useState<Container[]>(
    []
  );
  const [loadingContainers, setLoadingContainers] = useState(false);
  const [selectedContainerId, setSelectedContainerId] = useState<number | null>(
    null
  );
  const [linkingContainer, setLinkingContainer] = useState(false);

  const deliveryForm = Form.useForm()[0];

  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [deletingTaskId, setDeletingTaskId] = useState<number | null>(null);
  const [processingTaskAction, setProcessingTaskAction] = useState(false);

  // Add state to track form changes
  const [formChanged, setFormChanged] = useState<Record<string, boolean>>({
    general: false,
    supplier: false,
    delivery: false,
  });

  // Add state for other documents
  const [otherDocumentFile, setOtherDocumentFile] = useState<File | null>(null);
  const [uploadingOtherDocument, setUploadingOtherDocument] = useState(false);

  // Add state for product import
  const [showProductImport, setShowProductImport] = useState(false);
  const [otherDocuments, setOtherDocuments] = useState<
    Array<{
      id: number;
      booking_other_documents_file: string;
      created_at: string;
    }>
  >([]);
  const [otherDocumentPreviewVisible, setOtherDocumentPreviewVisible] =
    useState(false);
  const [otherDocumentPreviewUrl, setOtherDocumentPreviewUrl] = useState("");
  const [loadingOtherDocuments, setLoadingOtherDocuments] = useState(false);
  const [deleteDocumentId, setDeleteDocumentId] = useState<number | null>(null);
  const [deletingDocument, setDeletingDocument] = useState(false);
  const [productToDelete, setProductToDelete] = useState<Product | null>(null);
  const [deleteProductModalVisible, setDeleteProductModalVisible] =
    useState(false);
  const [deletingProduct, setDeletingProduct] = useState(false);

  // Company users state
  const [companyUsers, setCompanyUsers] = useState<
    Array<{
      id: string;
      first_name?: string;
      last_name?: string;
      email?: string;
      is_admin?: boolean;
    }>
  >([]);
  const [loadingCompanyUsers, setLoadingCompanyUsers] = useState(false);

  // Sales invoices state
  const [salesInvoices, setSalesInvoices] = useState<any[]>([]);
  const [loadingSalesInvoices, setLoadingSalesInvoices] = useState(false);

  // Invoice creation modal state
  const [createInvoiceModalVisible, setCreateInvoiceModalVisible] =
    useState(false);

  // Get booking data
  let company,
    supplier,
    origin,
    destination,
    incoterm,
    agent,
    deliveryInfo = null as DeliveryInfo | null,
    products = [] as Product[],
    container;

  // Handle container_info (can be object or array)
  if (booking.container_id && booking.container_info) {
    if (Array.isArray(booking.container_info)) {
      container = booking.container_info[0];
    } else {
      container = booking.container_info;
    }
  }

  // Handle company_info (can be object or array)
  if (booking.company_info) {
    if (Array.isArray(booking.company_info)) {
      company = booking.company_info[0];
    } else {
      company = booking.company_info;
    }
  }

  // Handle supplier_info (can be object or array)
  if (booking.supplier_info) {
    if (Array.isArray(booking.supplier_info)) {
      supplier = booking.supplier_info[0];
    } else {
      supplier = booking.supplier_info;
    }
  }

  // Handle origin_port_info (can be object or array)
  if (booking.origin_port_info) {
    if (Array.isArray(booking.origin_port_info)) {
      origin = booking.origin_port_info[0];
    } else {
      origin = booking.origin_port_info;
    }
  }

  // Handle destination_port_info (can be object or array)
  if (booking.destination_port_info) {
    if (Array.isArray(booking.destination_port_info)) {
      destination = booking.destination_port_info[0];
    } else {
      destination = booking.destination_port_info;
    }
  }

  // Handle booking_incoterm_info (can be object or array)
  if (booking.booking_incoterm_info) {
    if (Array.isArray(booking.booking_incoterm_info)) {
      incoterm = booking.booking_incoterm_info[0];
    } else {
      incoterm = booking.booking_incoterm_info;
    }
  }

  // Handle agent_info (can be object or array)
  if (booking.agent_info) {
    if (Array.isArray(booking.agent_info)) {
      agent = booking.agent_info[0];
    } else {
      agent = booking.agent_info;
    }
  }

  // Handle booking_delivery_info (can be object or array)
  if (booking.booking_delivery_info) {
    if (Array.isArray(booking.booking_delivery_info)) {
      deliveryInfo = booking.booking_delivery_info[0] as DeliveryInfo;
    } else {
      deliveryInfo = booking.booking_delivery_info as DeliveryInfo;
    }
  }

  // Handle product_info (can be object or array)
  if (booking.product_info) {
    if (Array.isArray(booking.product_info)) {
      // Handle nested array case first
      if (
        booking.product_info.length > 0 &&
        Array.isArray(booking.product_info[0])
      ) {
        products = booking.product_info[0];
      } else {
        products = booking.product_info;
      }
    } else {
      products = [booking.product_info];
    }
  } else {
    products = [];
  }

  // Load booking invoices, other documents, and agents on component mount or refresh
  useEffect(() => {
    if (!booking) return; // Skip if no booking

    // Reset form states when booking changes
    form.resetFields();
    productForm.resetFields();
    supplierForm.resetFields();
    deliveryForm.resetFields();
    taskForm.resetFields();

    // Reset all editing states
    setEditMode(false);
    setEditTarget(null);
    setIsEditingSupplier(false);
    setIsEditingDelivery(false);

    // Reset all file upload states
    setInvoiceFile(null);
    setBookingInvoiceFile(null);
    setBookingInvoiceDescription("");
    setOtherDocumentFile(null);

    // Reset modal visibilities
    setInvoicePreviewVisible(false);
    setBookingInvoicePreviewVisible(false);
    setOtherDocumentPreviewVisible(false);
    setIsProductModalVisible(false);
    setIsTaskModalVisible(false);

    // Reset delivery type based on booking
    setDeliveryType(booking.booking_delivery_info ? "address" : "storage");

    // Reset other form-related states
    setRejectionReason("");
    setSelectedAgentId(booking?.agent_id || null);

    const fetchBookingInvoices = async () => {
      setLoadingInvoices(true);
      try {
        const result = await getBookingInvoices(booking.id);
        if (result.data) {
          setBookingInvoices(result.data);
        }
      } catch (error) {
        console.error("Error fetching booking invoices:", error);
        message.error("Failed to load booking invoices");
      } finally {
        setLoadingInvoices(false);
      }
    };

    // Fetch other documents
    const fetchOtherDocuments = async () => {
      setLoadingOtherDocuments(true);
      try {
        const result = await getOtherDocuments(booking.id);
        if (result.data) {
          setOtherDocuments(result.data);
        }
      } catch (error) {
        console.error("Error fetching other documents:", error);
        message.error("Failed to load other documents");
      } finally {
        setLoadingOtherDocuments(false);
      }
    };

    // Fetch agents
    const fetchAvailableAgents = async () => {
      setLoadingAgents(true);
      try {
        const result = await fetchAgents();
        if (result.data) {
          setAgents(result.data);
        }
      } catch (error) {
        console.error("Error fetching agents:", error);
        message.error("Failed to load agents");
      } finally {
        setLoadingAgents(false);
      }
    };

    // Fetch tasks related to this booking
    const fetchBookingTasks = async () => {
      setLoadingTasks(true);
      try {
        const { data, error } = await supabase
          .from("tasks")
          .select(
            `
            *,
            team_members:team_member (id, name)
            `
          )
          .eq("booking_id", booking.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setTasks(data || []);
      } catch (error) {
        console.error("Error fetching tasks:", error);
        message.error("Failed to load tasks");
      } finally {
        setLoadingTasks(false);
      }
    };

    // Fetch team members for task assignment
    const fetchTeamMembers = async () => {
      try {
        const { data, error } = await supabase
          .from("team_members")
          .select("id, name")
          .order("name");

        if (error) throw error;

        if (data) {
          const options = data.map((member) => ({
            label: member.name,
            value: member.id,
          }));
          setMemberOptions(options);
        }
      } catch (error) {
        console.error("Error fetching team members:", error);
        message.error("Failed to load team members");
      }
    };

    // Fetch containers
    const fetchAvailableContainers = async () => {
      setLoadingContainers(true);
      try {
        const result = await fetchContainers();
        if (result.data) {
          setAvailableContainers(result.data);
        }
      } catch (error) {
        console.error("Error fetching containers:", error);
        message.error("Failed to load containers");
      } finally {
        setLoadingContainers(false);
      }
    };

    const fetchCompanyUsers = async (companyId: number) => {
      setLoadingCompanyUsers(true);
      try {
        const { data, error } = await getCompanyUsers(companyId);
        if (!error && data) {
          // The endpoint should return users array
          setCompanyUsers(data.users || data || []);
        } else {
          console.error("Error fetching company users:", error);
          setCompanyUsers([]);
        }
      } catch (error) {
        console.error("Error fetching company users:", error);
        setCompanyUsers([]);
      } finally {
        setLoadingCompanyUsers(false);
      }
    };

    // Fetch sales invoices for this booking
    const fetchSalesInvoices = async () => {
      setLoadingSalesInvoices(true);
      try {
        const { data: invoices, error } = await supabase
          .from("booking_sales_invoice")
          .select(
            `
            *,
            lines:booking_sales_invoice_line(*),
            booking:bookings(
              id,
              reference,
              transport_type_info:booking_transport_types(*)
            )
          `
          )
          .eq("booking_id", booking.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setSalesInvoices(invoices || []);
      } catch (error) {
        console.error("Error fetching sales invoices:", error);
        message.error("Failed to load sales invoices");
        setSalesInvoices([]);
      } finally {
        setLoadingSalesInvoices(false);
      }
    };

    fetchAvailableAgents();
    fetchBookingInvoices();
    fetchOtherDocuments();
    fetchBookingTasks();
    fetchTeamMembers();
    fetchAvailableContainers();
    fetchSalesInvoices();

    // Fetch company users if company ID exists
    if (booking.company_id) {
      fetchCompanyUsers(booking.company_id);
    }
  }, [booking?.id]);

  // Return null if booking is not available
  if (!booking) return null;

  // Generate a formatted booking reference
  const getFormattedBookingReference = (booking: Booking) => {
    // Get transport type abbreviation
    const transportInfo = booking.transport_type_info;
    let transportAbbr = "";

    if (transportInfo) {
      if (Array.isArray(transportInfo) && transportInfo.length > 0) {
        transportAbbr = transportInfo[0].abbreviation || "";
      } else if (typeof transportInfo === "object") {
        transportAbbr = (transportInfo as TransportType).abbreviation || "";
      }
    }

    // Format: EFF<booking_id><transport_type.abbreviation>
    return `EFF${booking.id}${transportAbbr}`;
  };

  // Product columns for the product table
  const productColumns: ColumnsType<Product> = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "EAN",
      dataIndex: "ean",
      key: "ean",
    },
    {
      title: "Pieces",
      dataIndex: "pieces",
      key: "pieces",
    },
    {
      title: "Cartons",
      dataIndex: "cartons",
      key: "cartons",
    },
    {
      title: "Gross Weight (kg)",
      dataIndex: "gross_weight",
      key: "gross_weight",
    },
    {
      title: "CBM",
      dataIndex: "cbm",
      key: "cbm",
    },
    {
      title: "HS Code",
      dataIndex: "hs_code",
      key: "hs_code",
    },
    {
      title: "Actions",
      key: "actions",
      width: 300,
      render: (_, record) => (
        <Space size="middle">
          <Button
            type="link"
            onClick={() => handleEditProduct(record)}
            icon={<EditOutlined />}
          >
            Edit
          </Button>
          {record.shipping_label && (
            <Button
              type="link"
              onClick={() => handleDownloadShippingLabel(record)}
              icon={<DownloadOutlined />}
            >
              Label
            </Button>
          )}
          <Button
            type="link"
            onClick={() => handleRegenerateShippingLabel(record)}
            icon={<ReloadOutlined />}
          >
            {record.shipping_label ? "Regenerate" : "Generate Label"}
          </Button>
          <Button
            type="link"
            danger
            onClick={() => handleDeleteProductClick(record)}
            icon={<DeleteOutlined />}
          >
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  // Form initial values
  const initialValues = {
    reference: booking.reference || "",
    notes: booking.notes || "",
    status: booking.status || "",
    origin_pickup_date: booking.origin_pickup_date
      ? dayjs(booking.origin_pickup_date)
      : null,
    transport_type: booking.transport_type || undefined,
    booking_type: booking.booking_type || undefined,
    booking_incoterm: booking.booking_incoterm || undefined,
  };

  // Function to handle clicking the Edit button
  const handleEditClick = (target: "general" | "supplier" | "delivery") => {
    setEditTarget(target);

    if (target === "general") {
      setEditMode(true);
    } else if (target === "supplier") {
      handleEditSupplier();
    } else if (target === "delivery") {
      handleEditDelivery();
    }
  };

  // Handle initiating supplier edit
  const handleEditSupplier = () => {
    if (supplier) {
      supplierForm.setFieldsValue({
        name: supplier.name || "",
        email: supplier.email || "",
        phone: supplier.phone || "",
        address: supplier.address || "",
        country: supplier.country || "",
        province: supplier.province || "",
      });
      setIsEditingSupplier(true);
    }
  };

  // Handle initiating delivery edit
  const handleEditDelivery = () => {
    if (deliveryInfo) {
      setEditingDeliveryId(deliveryInfo.id);
      deliveryForm.setFieldsValue({
        street_name: deliveryInfo.street_name || "",
        house_number: deliveryInfo.house_number || "",
        city: deliveryInfo.city || "",
        postal_code: deliveryInfo.postal_code || "",
        country: deliveryInfo.country || "",
        instructions: deliveryInfo.instructions || "",
      });
    } else {
      // Creating new delivery info
      setEditingDeliveryId(null);
      deliveryForm.resetFields();
    }
    setIsEditingDelivery(true);
  };

  // Handle canceling delivery edit
  const handleCancelDeliveryEdit = () => {
    setIsEditingDelivery(false);
    setEditingDeliveryId(null);
    deliveryForm.resetFields();
    setFormChanged((prev) => ({ ...prev, delivery: false }));
  };

  // Handle saving delivery changes
  const handleSaveDelivery = async () => {
    try {
      if (deliveryType === "storage") {
        // If switching to warehouse storage, show confirmation before deleting delivery info
        if (editingDeliveryId) {
          Modal.confirm({
            title: "Confirm Warehouse Storage",
            content:
              "This will remove the delivery address. Are you sure you want to proceed?",
            okText: "Yes, Proceed",
            cancelText: "Cancel",
            onOk: async () => {
              setProcessingDelivery(true);
              try {
                const result = await deleteDeliveryInfo(editingDeliveryId);

                if (result.error) {
                  message.error("Failed to update delivery information");
                  return;
                }

                message.success(
                  "Delivery information updated. Set to warehouse storage."
                );
                setIsEditingDelivery(false);
                setFormChanged((prev) => ({ ...prev, delivery: false }));
                onRefresh(); // Refresh booking data
              } catch (error) {
                console.error("Error updating delivery information:", error);
                message.error(
                  "An error occurred while updating delivery information"
                );
              } finally {
                setProcessingDelivery(false);
              }
            },
          });
          return;
        } else {
          // No delivery info to delete, just close the edit mode
          message.success("Set to warehouse storage");
          setIsEditingDelivery(false);
          setFormChanged((prev) => ({ ...prev, delivery: false }));
          return;
        }
      }

      // Validate form for address delivery
      const values = await deliveryForm.validateFields();
      setProcessingDelivery(true);

      if (editingDeliveryId) {
        // Update existing delivery info
        const result = await updateDeliveryInfo(editingDeliveryId, values);

        if (result.error) {
          message.error("Failed to update delivery information");
          return;
        }
      } else {
        // Create new delivery info
        const result = await createDeliveryInfo(booking.id, values);

        if (result.error) {
          message.error("Failed to create delivery information");
          return;
        }
      }

      message.success("Delivery information updated successfully");
      setIsEditingDelivery(false);
      setFormChanged((prev) => ({ ...prev, delivery: false }));
      onRefresh(); // Refresh booking data
    } catch (error) {
      console.error("Error updating delivery information:", error);
      message.error("An error occurred while updating delivery information");
    } finally {
      setProcessingDelivery(false);
    }
  };

  // Add handlers for form changes
  const handleFormChange = () => {
    const hasChanged = form.isFieldsTouched();
    setFormChanged((prev) => ({ ...prev, general: hasChanged }));
  };

  const handleSupplierFormChange = () => {
    const hasChanged = supplierForm.isFieldsTouched();
    setFormChanged((prev) => ({ ...prev, supplier: hasChanged }));
  };

  const handleDeliveryFormChange = () => {
    const hasChanged = deliveryForm.isFieldsTouched();
    setFormChanged((prev) => ({ ...prev, delivery: hasChanged }));
  };

  // Handle save
  const handleSave = async () => {
    try {
      // Validate form
      const values = await form.validateFields();
      setSaving(true);

      const result = await updateBookingDetails(booking.id, {
        reference: values.reference.trim() || null,
        notes: values.notes.trim() || null,
        status: values.status || null,
        origin_pickup_date: values.origin_pickup_date
          ? values.origin_pickup_date.format("YYYY-MM-DD")
          : null,
        transport_type: values.transport_type || null,
        booking_type: values.booking_type || null,
        booking_incoterm: values.booking_incoterm || null,
      });

      if (result.error) {
        message.error("Failed to update booking details");
        return;
      }

      message.success("Booking details updated successfully");
      trackFeatureUsage("admin_booking_details_edit");
      setEditMode(false);
      setEditTarget(null);
      setFormChanged((prev) => ({ ...prev, general: false }));
      onRefresh();
    } catch (error) {
      console.error("Error updating booking details:", error);
      message.error("An error occurred while updating booking details");
    } finally {
      setSaving(false);
    }
  };

  // Handle cancel
  const handleCancel = () => {
    form.resetFields();
    setEditMode(false);
    setEditTarget(null);
    setFormChanged((prev) => ({ ...prev, general: false }));
  };

  // Handle supplier save
  const handleSaveSupplier = async () => {
    try {
      // Validate supplier form
      const supplierValues = await supplierForm.validateFields();
      setSavingSupplier(true);

      if (supplier?.id) {
        const result = await updateBookingSupplier(supplier.id, supplierValues);

        if (result.error) {
          message.error("Failed to update supplier information");
          return;
        }

        message.success("Supplier information updated successfully");
        setFormChanged((prev) => ({ ...prev, supplier: false }));
        // Call the onRefresh function to reload the booking
        onRefresh();
      }
    } catch (validationError) {
      console.error("Validation error:", validationError);
    } finally {
      setSavingSupplier(false);
      setIsEditingSupplier(false);
    }
  };

  // Handle canceling supplier edit
  const handleCancelSupplierEdit = () => {
    setIsEditingSupplier(false);
    supplierForm.resetFields();
    setFormChanged((prev) => ({ ...prev, supplier: false }));
  };

  // Task functions
  const handleAddTask = () => {
    taskForm.resetFields();
    taskForm.setFieldsValue({
      company_id: booking.company_id,
      booking_id: booking.id,
    });
    setIsTaskModalVisible(true);
  };

  const handleSaveTask = async () => {
    try {
      const values = await taskForm.validateFields();
      setProcessingTaskAction(true);

      const taskData = {
        booking_id: booking.id,
        company_id: booking.company_id,
        date: values.date ? values.date.toISOString() : null,
        name: values.name,
        description: values.description || null,
        team_member: values.team_member,
        completed: values.completed || false,
        completed_on: values.completed ? new Date().toISOString() : null,
      };

      if (editingTask) {
        // Update existing task
        const { error } = await supabase
          .from("tasks")
          .update(taskData)
          .eq("id", editingTask.id);

        if (error) throw error;
        message.success("Task updated successfully");
      } else {
        // Create new task
        const { error } = await supabase.from("tasks").insert(taskData);

        if (error) throw error;
        message.success("Task created successfully");
      }

      setIsTaskModalVisible(false);
      setEditingTask(null);

      // Refresh the tasks list
      const { data, error: fetchError } = await supabase
        .from("tasks")
        .select(
          `
          *,
          team_members:team_member (id, name)
          `
        )
        .eq("booking_id", booking.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setTasks(data || []);

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error saving task:", error);
      message.error("Failed to save task");
    } finally {
      setProcessingTaskAction(false);
    }
  };

  const handleCancelTask = () => {
    setIsTaskModalVisible(false);
    setEditingTask(null);
    taskForm.resetFields();
  };

  const handleEditTask = (task: Task) => {
    setEditingTask(task);
    taskForm.setFieldsValue({
      company_id: booking.company_id,
      booking_id: booking.id,
      name: task.name,
      description: task.description,
      date: task.date ? dayjs(task.date) : null,
      team_member: task.team_member,
      completed: task.completed,
    });
    setIsTaskModalVisible(true);
  };

  const handleDeleteTask = async () => {
    if (!deletingTaskId) return;

    try {
      setProcessingTaskAction(true);
      const { error } = await supabase
        .from("tasks")
        .delete()
        .eq("id", deletingTaskId);

      if (error) throw error;

      message.success("Task deleted successfully");

      // Refresh the tasks list
      const { data, error: fetchError } = await supabase
        .from("tasks")
        .select(
          `
          *,
          team_members:team_member (id, name)
          `
        )
        .eq("booking_id", booking.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setTasks(data || []);

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error deleting task:", error);
      message.error("Failed to delete task");
    } finally {
      setProcessingTaskAction(false);
      setDeletingTaskId(null);
    }
  };

  const handleCompleteTask = async (taskId: number) => {
    try {
      setProcessingTaskAction(true);
      const { error } = await supabase
        .from("tasks")
        .update({
          completed: true,
          completed_on: new Date().toISOString(),
        })
        .eq("id", taskId);

      if (error) throw error;

      message.success("Task marked as complete");

      // Refresh the tasks list
      const { data, error: fetchError } = await supabase
        .from("tasks")
        .select(
          `
          *,
          team_members:team_member (id, name)
          `
        )
        .eq("booking_id", booking.id)
        .order("created_at", { ascending: false });

      if (fetchError) throw fetchError;
      setTasks(data || []);

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error completing task:", error);
      message.error("Failed to complete task");
    } finally {
      setProcessingTaskAction(false);
    }
  };

  // Define task columns for the table
  const taskColumns = [
    {
      title: "Task Name",
      dataIndex: "name",
      key: "name",
      width: "25%",
      render: (text: string) => <strong>{text}</strong>,
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      width: "30%",
      render: (text: string | null) => text || "-",
    },
    {
      title: "Due Date",
      dataIndex: "date",
      key: "date",
      width: "15%",
      render: (date: string | null) => (date ? formatDate(date) : "-"),
    },
    {
      title: "Assigned To",
      dataIndex: "team_members",
      key: "team_member",
      width: "15%",
      render: (team_member: { id: number; name: string } | null) =>
        team_member ? team_member.name : "-",
    },
    {
      title: "Status",
      dataIndex: "completed",
      key: "completed",
      width: "15%",
      render: (completed: boolean, record: Task) => (
        <>
          {completed ? (
            <Tag color="green" icon={<CheckCircleOutlined />}>
              Completed
            </Tag>
          ) : record.date && new Date(record.date) < new Date() ? (
            <Tag color="red">Overdue</Tag>
          ) : (
            <Tag color="orange" icon={<ClockCircleOutlined />}>
              Pending
            </Tag>
          )}
        </>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: "15%",
      render: (_: unknown, record: Task) => (
        <Space size="small">
          {!record.completed && (
            <Tooltip title="Mark Complete">
              <Button
                type="primary"
                size="small"
                icon={<CheckCircleOutlined />}
                onClick={() => handleCompleteTask(record.id)}
              />
            </Tooltip>
          )}
          <Tooltip title="Edit">
            <Button
              type="default"
              size="small"
              icon={<EditOutlined />}
              onClick={() => handleEditTask(record)}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              danger
              size="small"
              icon={<DeleteOutlined />}
              onClick={() => setDeletingTaskId(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Function to check if there are any pending tasks
  const hasPendingTasks = () => {
    return tasks.some((task) => !task.completed);
  };

  // Handle product edit
  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    productForm.setFieldsValue({
      name: product.name,
      hs_code: product.hs_code,
      ean: product.ean,
      pieces: product.pieces,
      cartons: product.cartons,
      gross_weight: product.gross_weight,
      cbm: product.cbm,
      value: product.value || 0,
    });
    setIsProductModalVisible(true);
  };

  // Handle product save
  const handleSaveProduct = async () => {
    try {
      const values = await productForm.validateFields();
      setSavingProduct(true);

      if (editingProduct?.id) {
        const result = await updateBookingProduct(editingProduct.id, values);

        if (result.error) {
          message.error("Failed to update product");
          return;
        }

        message.success("Product updated successfully");
        onRefresh();
      }
    } catch (error) {
      console.error("Error updating product:", error);
      message.error("An error occurred while updating product");
    } finally {
      setSavingProduct(false);
      setIsProductModalVisible(false);
      setEditingProduct(null);
    }
  };

  // Handle shipping label download
  const handleDownloadShippingLabel = async (product: Product) => {
    if (product.shipping_label) {
      try {
        // Get a signed URL for the file
        const { data, error } = await supabase.storage
          .from("shipping_labels")
          .createSignedUrl(product.shipping_label, 60); // URL valid for 60 seconds

        if (error) {
          console.error("Error creating signed URL:", error);
          message.error("Failed to download shipping label");
          return;
        }

        if (!data?.signedUrl) {
          message.error("Failed to generate download URL");
          return;
        }

        // Download the file using the signed URL
        await downloadFile(data.signedUrl, `shipping-label-${product.id}.pdf`);
      } catch (error) {
        console.error("Error downloading shipping label:", error);
        message.error("Failed to download shipping label");
      }
    } else {
      message.info("No shipping label available for this product");
    }
  };

  // Handle shipping label regeneration
  const handleRegenerateShippingLabel = async (product: Product) => {
    try {
      message.loading({
        content: "Generating shipping label...",
        key: "labelGeneration",
      });

      // Get company name from the component
      const currentCompanyName = company?.name || "Your Company";

      // Create a supplier object from the booking data
      let supplierData = null;
      if (supplier) {
        supplierData = {
          name: supplier.name || "",
          email: supplier.email || "",
          phone: supplier.phone || "",
          country: supplier.country || "",
          city: "", // Default empty if not available
          address: supplier.address || "",
        };
      } else {
        // Create a default supplier to prevent errors
        supplierData = {
          name: "Supplier information unavailable",
          email: "",
          phone: "",
          country: "",
          city: "",
          address: "",
        };
      }

      // Create a transport type object if available
      let transportType = { name: "Unknown", abbreviation: "" };
      if (booking.transport_type_info) {
        const transportInfo = Array.isArray(booking.transport_type_info)
          ? booking.transport_type_info[0]
          : booking.transport_type_info;

        if (transportInfo) {
          transportType = {
            name: transportInfo.name || "Unknown",
            abbreviation: transportInfo.abbreviation || "",
          };
        }
      }

      // Create origin and destination ports if available
      let originPort = undefined;
      if (booking.origin_port_info) {
        const portInfo = Array.isArray(booking.origin_port_info)
          ? booking.origin_port_info[0]
          : booking.origin_port_info;

        if (portInfo) {
          originPort = {
            name: portInfo.name || "",
            country: portInfo.country || "",
          };
        }
      }

      let destinationPort = undefined;
      if (booking.destination_port_info) {
        const portInfo = Array.isArray(booking.destination_port_info)
          ? booking.destination_port_info[0]
          : booking.destination_port_info;

        if (portInfo) {
          destinationPort = {
            name: portInfo.name || "",
            country: portInfo.country || "",
          };
        }
      }

      // Create a booking incoterm if available
      let bookingIncoterm = { name: "N/A" };
      if (booking.booking_incoterm_info) {
        const incotermInfo = Array.isArray(booking.booking_incoterm_info)
          ? booking.booking_incoterm_info[0]
          : booking.booking_incoterm_info;

        if (incotermInfo) {
          bookingIncoterm = {
            name: incotermInfo.name || "N/A",
          };
        }
      }

      // Prepare the booking object in the expected format
      const adaptedBooking = {
        id: booking.id,
        reference: booking.reference || "",
        transport_type: transportType,
        booking_type: booking.booking_type_info
          ? {
              name: Array.isArray(booking.booking_type_info)
                ? booking.booking_type_info[0]?.name || ""
                : booking.booking_type_info.name || "",
            }
          : undefined,
        booking_incoterm: bookingIncoterm,
        origin_port: originPort,
        destination_port: destinationPort,
        supplier: supplierData,
      };

      // The function returns the file path string on success or null on error
      const labelPath = await generateAndSaveShippingLabel(
        adaptedBooking,
        {
          ...product,
          company_id: booking.company_id || 0, // Add company_id from booking if missing in product
        },
        currentCompanyName
      );

      // Check if the operation was successful
      if (!labelPath) {
        message.error({
          content: "Failed to generate shipping label",
          key: "labelGeneration",
        });
        return;
      }

      // Update the product's shipping_label property
      try {
        // First update the local product in our database
        const { error } = await supabase
          .from("booking_product") // Use booking_product table
          .update({ shipping_label: labelPath })
          .eq("id", product.id);

        if (error) {
          console.error("Error updating product with shipping label:", error);
          // Continue despite error, the label was generated successfully
        }
      } catch (updateError) {
        console.error("Error updating product shipping label:", updateError);
        // Continue despite error, the label was generated successfully
      }

      message.success({
        content: "Shipping label generated successfully",
        key: "labelGeneration",
      });
      onRefresh();
    } catch (error) {
      console.error("Error generating shipping label:", error);
      message.error({
        content: "An error occurred while generating shipping label",
        key: "labelGeneration",
      });
    }
  };

  // Handle agent change
  const handleAgentChange = async (value: number | null) => {
    setSelectedAgentId(value);

    if (booking?.id) {
      setUpdatingAgent(true);
      try {
        const result = await updateBookingAgent(booking.id, value);

        if (result.error) {
          message.error("Failed to update agent");
          return;
        }

        message.success("Agent updated successfully");
        onRefresh();
      } catch (error) {
        console.error("Error updating agent:", error);
        message.error("An error occurred while updating agent");
      } finally {
        setUpdatingAgent(false);
      }
    }
  };

  // Handle booking approval button click (show modal)
  const handleApproveBooking = () => {
    // Check agent assignment first
    if (!booking.agent_id && agents.length === 0) {
      message.error("Please assign an agent before approving the booking");
      return;
    }
    setShowApprovalModal(true);
  };

  // Handle actual booking approval with notes
  const handleApproveBookingWithNotes = async () => {
    // If no agent is assigned but agents are available, auto-select the first agent
    if (!booking.agent_id && agents.length > 0) {
      setUpdatingAgent(true);
      try {
        const firstAgent = agents[0];
        const agentResult = await updateBookingAgent(booking.id, firstAgent.id);

        if (agentResult.error) {
          message.error("Failed to assign agent automatically");
          return;
        }

        setSelectedAgentId(firstAgent.id);
        message.info(
          `Agent "${firstAgent.name}" was automatically assigned to this booking`
        );
      } catch (error) {
        console.error("Error assigning agent:", error);
        message.error("Failed to assign agent automatically");
        return;
      } finally {
        setUpdatingAgent(false);
      }
    } else if (!booking.agent_id && agents.length === 0) {
      message.error("Please assign an agent before approving the booking");
      return;
    }

    setApprovingBooking(true);
    try {
      // Check if this is a storage booking (no delivery address)
      const hasDeliveryAddress =
        booking.booking_delivery_info &&
        (Array.isArray(booking.booking_delivery_info)
          ? booking.booking_delivery_info.length > 0
          : true);

      const isStorageBooking = !hasDeliveryAddress;

      // Update booking status and notes if provided
      const updateData: any = {
        status: BookingStatus.APPROVED,
      };

      // Only add notes if they were provided
      if (approvalNotes.trim()) {
        // Append to existing notes or create new ones
        const existingNotes = booking.notes || "";
        const newNotes = existingNotes
          ? `${existingNotes}\n\nApproval Notes: ${approvalNotes.trim()}`
          : `Approval Notes: ${approvalNotes.trim()}`;
        updateData.notes = newNotes;
      }

      const result = await updateBookingDetails(booking.id, updateData);

      if (result.error) {
        message.error("Failed to approve booking");
        return;
      }

      // Purchase orders are now created manually after approval
      // If this is a storage booking, show info about manual creation
      if (isStorageBooking) {
        message.info(
          "Storage booking approved. You can now create a purchase order manually in the Purchase Orders tab if needed."
        );
      }

      message.success("Booking approved successfully");
      trackFeatureUsage("admin_booking_approve", {
        isStorageBooking,
        purchaseOrderCreated: false, // Now manual
      });
      setShowApprovalModal(false);
      setApprovalNotes("");
      onRefresh();
    } catch (error) {
      console.error("Error approving booking:", error);
      message.error("An error occurred while approving booking");
    } finally {
      setApprovingBooking(false);
    }
  };

  // Handle booking rejection
  const handleRejectBooking = async () => {
    if (!rejectionReason.trim()) {
      message.error("Please provide a reason for rejection");
      return;
    }

    setRejectingBooking(true);
    try {
      const result = await updateBookingStatus(
        booking.id,
        BookingStatus.REJECTED,
        {
          rejection_reason: rejectionReason,
        }
      );

      if (result.error) {
        message.error("Failed to reject booking");
        return;
      }

      message.success("Booking rejected successfully");
      trackFeatureUsage("admin_booking_reject");
      setShowRejectModal(false);
      setRejectionReason("");
      onRefresh();
    } catch (error) {
      console.error("Error rejecting booking:", error);
      message.error("An error occurred while rejecting booking");
    } finally {
      setRejectingBooking(false);
    }
  };

  // Mark booking as completed when shipment is successfully created
  const markBookingAsCompleted = async () => {
    try {
      const { error } = await supabase
        .from("bookings")
        .update({
          unloading_status: "completed",
          unloaded_at: new Date().toISOString(),
        })
        .eq("id", booking.id);

      if (error) throw error;

      message.success(
        "Booking marked as completed! Shipment created successfully. You can now download shipping labels."
      );

      // Refresh the booking data
      onRefresh();
    } catch (error) {
      console.error("Error marking booking as completed:", error);
      message.error("Failed to mark booking as completed");
    }
  };

  // Parse commercial invoice files
  const commercialInvoiceFiles = booking.commercial_invoice_file
    ? booking.commercial_invoice_file.split(",")
    : [];

  // Handle commercial invoice upload
  const uploadProps = {
    beforeUpload: (file: File) => {
      const isValidType =
        file.type === "application/pdf" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error("Only PDF, Word, or Excel files are allowed");
        return Upload.LIST_IGNORE;
      }

      if (!isLt10M) {
        message.error("File must be smaller than 10MB");
        return Upload.LIST_IGNORE;
      }

      setInvoiceFile(file);
      return false;
    },
    fileList: invoiceFile
      ? [{ uid: "1", name: invoiceFile.name, status: "done" }]
      : [],
    onRemove: () => {
      setInvoiceFile(null);
      return true;
    },
  };

  // Handle booking invoice upload props
  const bookingInvoiceUploadProps = {
    beforeUpload: (file: File) => {
      const isValidType =
        file.type === "application/pdf" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error("Only PDF, Word, or Excel files are allowed");
        return Upload.LIST_IGNORE;
      }

      if (!isLt10M) {
        message.error("File must be smaller than 10MB");
        return Upload.LIST_IGNORE;
      }

      setBookingInvoiceFile(file);
      return false;
    },
    fileList: bookingInvoiceFile
      ? [{ uid: "1", name: bookingInvoiceFile.name, status: "done" }]
      : [],
    onRemove: () => {
      setBookingInvoiceFile(null);
      return true;
    },
  };

  // Handle commercial invoice upload
  const handleCommercialInvoiceUpload = async () => {
    if (!invoiceFile) {
      message.error("Please select a file to upload");
      return;
    }

    setUploadingInvoice(true);
    try {
      // First upload the file to storage
      const uploadResult = await uploadCommercialInvoice(
        booking.id,
        invoiceFile,
        appendInvoice,
        booking.company_id
      );

      console.log("Upload result:", uploadResult);

      if (!uploadResult || uploadResult.error) {
        message.error("Failed to upload commercial invoice");
        console.error("Upload error:", uploadResult?.error);
        return;
      }

      // Then update the booking record with the new file path
      const result = await updateBookingCommercialInvoice(
        booking.id,
        uploadResult.path || "",
        appendInvoice
      );

      if (result.error) {
        message.error("Failed to update booking with commercial invoice");
        return;
      }

      message.success("Commercial invoice uploaded successfully");
      setInvoiceFile(null);
      onRefresh();
    } catch (error) {
      console.error("Error uploading commercial invoice:", error);
      message.error("An error occurred while uploading commercial invoice");
    } finally {
      setUploadingInvoice(false);
    }
  };

  // Handle commercial invoice download
  const handleDownloadCommercialInvoice = async (
    filePath?: string,
    index?: number
  ) => {
    try {
      const path =
        filePath ||
        (commercialInvoiceFiles.length > 0 ? commercialInvoiceFiles[0] : null);

      if (!path) {
        message.error("No commercial invoice file available");
        return;
      }

      const fileName =
        path.split("/").pop() ||
        `commercial-invoice-${index !== undefined ? index + 1 : ""}.pdf`;
      await downloadCommercialInvoice(path, fileName);
    } catch (error) {
      console.error("Error downloading commercial invoice:", error);
      message.error("An error occurred while downloading commercial invoice");
    }
  };

  // Handle commercial invoice preview
  const handleViewCommercialInvoice = async (filePath: string) => {
    try {
      const url = await createCommercialInvoiceSignedUrl(filePath);

      if (!url) {
        message.error("Failed to generate preview URL");
        return;
      }

      setInvoicePreviewUrl(url);
      setInvoicePreviewVisible(true);
    } catch (error) {
      console.error("Error generating preview URL:", error);
      message.error("An error occurred while generating preview URL");
    }
  };

  // Handle booking invoice upload
  const handleBookingInvoiceUpload = async () => {
    if (!bookingInvoiceFile) {
      message.error("Please select a file to upload");
      return;
    }

    setUploadingBookingInvoice(true);
    try {
      console.log("Starting booking invoice upload process...");
      console.log("Booking ID:", booking.id);
      console.log("Company ID:", booking.company_id);

      // First upload the file to storage
      const uploadResult = await uploadBookingInvoice(
        booking.id,
        bookingInvoiceFile,
        booking.company_id
      );

      console.log("Upload result:", uploadResult);

      if (!uploadResult || uploadResult.error) {
        message.error("Failed to upload booking invoice");
        console.error("Upload error:", uploadResult?.error);
        return;
      }

      // Then create a record in the database - without the description field
      console.log("Creating database record with path:", uploadResult.path);

      const createResult = await createBookingInvoice(
        booking.id,
        uploadResult.path || ""
      );

      console.log("Database record creation result:", createResult);

      if (createResult.error) {
        message.error("Failed to create booking invoice record");
        console.error("Database error:", createResult.error);
        return;
      }

      message.success("Booking invoice uploaded successfully");
      setBookingInvoiceFile(null);
      setBookingInvoiceDescription("");

      // Refresh booking invoices
      const fetchResult = await getBookingInvoices(booking.id);
      if (fetchResult.data) {
        setBookingInvoices(fetchResult.data);
      }

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error uploading booking invoice:", error);
      message.error("An error occurred while uploading booking invoice");
    } finally {
      setUploadingBookingInvoice(false);
    }
  };

  // Handle booking invoice download
  const handleDownloadBookingInvoice = async (
    filePath: string,
    invoiceId: number
  ) => {
    try {
      const fileName =
        filePath.split("/").pop() || `booking-invoice-${invoiceId}.pdf`;
      await downloadBookingInvoice(filePath, fileName);
    } catch (error) {
      console.error("Error downloading booking invoice:", error);
      message.error("An error occurred while downloading booking invoice");
    }
  };

  // Handle booking invoice preview
  const handleViewBookingInvoice = async (filePath: string) => {
    try {
      const url = await createBookingInvoiceSignedUrl(filePath);

      if (!url) {
        message.error("Failed to generate preview URL");
        return;
      }

      setBookingInvoicePreviewUrl(url);
      setBookingInvoicePreviewVisible(true);
    } catch (error) {
      console.error("Error generating preview URL:", error);
      message.error("An error occurred while generating preview URL");
    }
  };

  // Handle booking invoice deletion
  const handleDeleteBookingInvoice = async () => {
    if (!deleteInvoiceId) return;

    setDeletingInvoice(true);
    try {
      const result = await deleteBookingInvoice(deleteInvoiceId);

      if (result.error) {
        message.error("Failed to delete booking invoice");
        return;
      }

      message.success("Booking invoice deleted successfully");
      setDeleteInvoiceId(null);

      // Refresh booking invoices
      const fetchResult = await getBookingInvoices(booking.id);
      if (fetchResult.data) {
        setBookingInvoices(fetchResult.data);
      }

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error deleting booking invoice:", error);
      message.error("An error occurred while deleting booking invoice");
    } finally {
      setDeletingInvoice(false);
    }
  };

  // Handle other document upload props
  const otherDocumentUploadProps = {
    beforeUpload: (file: File) => {
      const isValidType =
        file.type === "application/pdf" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
        file.type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        file.type === "application/vnd.ms-excel";
      const isLt10M = file.size / 1024 / 1024 < 10;

      if (!isValidType) {
        message.error("Only PDF, Word, or Excel files are allowed");
        return Upload.LIST_IGNORE;
      }

      if (!isLt10M) {
        message.error("File must be smaller than 10MB");
        return Upload.LIST_IGNORE;
      }

      setOtherDocumentFile(file);
      return false;
    },
    fileList: otherDocumentFile
      ? [{ uid: "1", name: otherDocumentFile.name, status: "done" }]
      : [],
    onRemove: () => {
      setOtherDocumentFile(null);
      return true;
    },
  };

  // Handle other document upload
  const handleOtherDocumentUpload = async () => {
    if (!otherDocumentFile) {
      message.error("Please select a file to upload");
      return;
    }

    setUploadingOtherDocument(true);
    try {
      console.log("Starting other document upload process...");
      console.log("Booking ID:", booking.id);
      console.log("Company ID:", booking.company_id);

      // First upload the file to storage
      const uploadResult = await uploadOtherDocument(
        booking.id,
        otherDocumentFile,
        booking.company_id
      );

      console.log("Upload result:", uploadResult);

      if (!uploadResult || uploadResult.error) {
        message.error("Failed to upload other document");
        console.error("Upload error:", uploadResult?.error);
        return;
      }

      // Then create a record in the database - removed description parameter
      console.log("Creating database record with path:", uploadResult.path);

      const createResult = await createOtherDocument(
        booking.id,
        uploadResult.path || ""
      );

      console.log("Database record creation result:", createResult);

      if (createResult.error) {
        message.error("Failed to create other document record");
        console.error("Database error:", createResult.error);
        return;
      }

      message.success("Other document uploaded successfully");
      setOtherDocumentFile(null);

      // Refresh other documents
      const fetchResult = await getOtherDocuments(booking.id);
      if (fetchResult.data) {
        setOtherDocuments(fetchResult.data);
      }

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error uploading other document:", error);
      message.error("An error occurred while uploading other document");
    } finally {
      setUploadingOtherDocument(false);
    }
  };

  // Handle other document download
  const handleDownloadOtherDocument = async (
    filePath: string,
    documentId: number
  ) => {
    try {
      const fileName =
        filePath.split("/").pop() || `other-document-${documentId}.pdf`;
      await downloadOtherDocument(filePath, fileName);
    } catch (error) {
      console.error("Error downloading other document:", error);
      message.error("An error occurred while downloading other document");
    }
  };

  // Handle other document preview
  const handleViewOtherDocument = async (filePath: string) => {
    try {
      const url = await createOtherDocumentSignedUrl(filePath);

      if (!url) {
        message.error("Failed to generate preview URL");
        return;
      }

      setOtherDocumentPreviewUrl(url);
      setOtherDocumentPreviewVisible(true);
    } catch (error) {
      console.error("Error generating preview URL:", error);
      message.error("An error occurred while generating preview URL");
    }
  };

  // Handle other document deletion
  const handleDeleteOtherDocument = async () => {
    if (!deleteDocumentId) return;

    setDeletingDocument(true);
    try {
      const result = await deleteOtherDocument(deleteDocumentId);

      if (result.error) {
        message.error("Failed to delete other document");
        return;
      }

      message.success("Other document deleted successfully");
      setDeleteDocumentId(null);

      // Refresh other documents
      const fetchResult = await getOtherDocuments(booking.id);
      if (fetchResult.data) {
        setOtherDocuments(fetchResult.data);
      }

      // Call onRefresh to update the main booking data
      onRefresh();
    } catch (error) {
      console.error("Error deleting other document:", error);
      message.error("An error occurred while deleting other document");
    } finally {
      setDeletingDocument(false);
    }
  };

  // Add hooks to fetch transport types, booking types, and incoterms
  const { transportTypes, loading: loadingTransportTypes } =
    useTransportTypes();
  const { bookingTypes, loading: loadingBookingTypes } = useBookingTypes();
  const { incoterms, loading: loadingIncoterms } = useIncoterms();

  // Helper to notify parent about changes
  const notifyChanges = useCallback(
    (hasChanges: boolean) => {
      if (onChangesMade) {
        onChangesMade(hasChanges);
      }
    },
    [onChangesMade]
  );

  // Track form changes
  useEffect(() => {
    // Check if any form has changes
    const hasAnyChanges = Object.values(formChanged).some((value) => value);
    notifyChanges(hasAnyChanges);
  }, [formChanged, notifyChanges]);

  // Reset form change tracking on mode change
  useEffect(() => {
    if (!editMode) {
      setFormChanged((prev) => ({ ...prev, general: false }));
    }
    if (!isEditingSupplier) {
      setFormChanged((prev) => ({ ...prev, supplier: false }));
    }
    if (!isEditingDelivery) {
      setFormChanged((prev) => ({ ...prev, delivery: false }));
    }
  }, [editMode, isEditingSupplier, isEditingDelivery]);

  // Handle linking a booking to a container
  const handleLinkToContainer = async () => {
    if (!selectedContainerId) {
      message.error("Please select a container");
      return;
    }

    setLinkingContainer(true);
    try {
      const result = await linkBookingToContainer(
        booking.id,
        selectedContainerId
      );

      if (result.error) {
        message.error("Failed to link booking to container");
        return;
      }

      message.success("Booking successfully linked to container");
      onRefresh();
    } catch (error) {
      console.error("Error linking booking to container:", error);
      message.error("An error occurred while linking to container");
    } finally {
      setLinkingContainer(false);
    }
  };

  // Add navigation
  const navigate = useNavigate();

  // Add function to navigate to company admin page
  const handleViewCompany = () => {
    if (!booking.company_id) {
      message.error("No company information available");
      return;
    }

    // Track feature usage
    trackFeatureUsage("view_company_from_booking");

    // Navigate to companies admin page with ID parameter
    navigate(`/admin/companies?id=${booking.company_id}`);
  };

  // Handle product deletion click (opens confirmation modal)
  const handleDeleteProductClick = (product: Product) => {
    setProductToDelete(product);
    setDeleteProductModalVisible(true);
  };

  // Handle product deletion confirmation
  const handleDeleteProduct = async () => {
    if (!productToDelete) return;

    try {
      setDeletingProduct(true);

      // Import the deleteBookingProduct function at the top of the file if not already imported
      const { success, error } = await deleteBookingProduct(productToDelete.id);

      if (error) {
        message.error("Failed to delete product");
        return;
      }

      message.success("Product deleted successfully");
      setDeleteProductModalVisible(false);
      setProductToDelete(null);
      onRefresh(); // Refresh booking data to update the products list

      // Signal that changes were made
      if (onChangesMade) {
        onChangesMade(true);
      }
    } catch (error) {
      console.error("Error deleting product:", error);
      message.error("Failed to delete product");
    } finally {
      setDeletingProduct(false);
    }
  };

  // Handle create invoice success
  const handleCreateInvoiceSuccess = (invoice: Invoice) => {
    message.success("Invoice created successfully");
    setCreateInvoiceModalVisible(false);

    // Refresh sales invoices
    const fetchSalesInvoices = async () => {
      setLoadingSalesInvoices(true);
      try {
        const { data: invoices, error } = await supabase
          .from("booking_sales_invoice")
          .select(
            `
            *,
            lines:booking_sales_invoice_line(*),
            booking:bookings(
              id,
              reference,
              transport_type_info:booking_transport_types(*)
            )
          `
          )
          .eq("booking_id", booking.id)
          .order("created_at", { ascending: false });

        if (error) throw error;
        setSalesInvoices(invoices || []);
      } catch (error) {
        console.error("Error fetching sales invoices:", error);
        setSalesInvoices([]);
      } finally {
        setLoadingSalesInvoices(false);
      }
    };

    fetchSalesInvoices();

    // Signal that changes were made
    if (onChangesMade) {
      onChangesMade(true);
    }
  };

  return (
    <>
      <Tabs defaultActiveKey={defaultTab} key={`booking-tabs-${booking.id}`}>
        <TabPane tab="General Information" key="1">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={() => setEditMode(true)}
              disabled={editMode}
            >
              Edit
            </Button>
          </div>
          <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            style={{ marginBottom: 16 }}
            onValuesChange={handleFormChange}
          >
            <Row gutter={[16, 16]}>
              <Col span={12}>
                <p>
                  <strong>Booking ID:</strong>{" "}
                  {getFormattedBookingReference(booking)}
                </p>
                <Form.Item
                  label={<strong>Reference:</strong>}
                  name="reference"
                  rules={[
                    {
                      max: 100,
                      message: "Reference number cannot exceed 100 characters",
                    },
                  ]}
                  style={{ marginBottom: 8 }}
                >
                  {editMode ? (
                    <Input
                      placeholder="Enter booking reference"
                      maxLength={100}
                    />
                  ) : (
                    <div>{booking.reference || "-"}</div>
                  )}
                </Form.Item>
                <p>
                  <strong>Status:</strong>{" "}
                  {editMode ? (
                    <Form.Item
                      name="status"
                      style={{ marginBottom: 0 }}
                      rules={[
                        { required: true, message: "Please select a status" },
                      ]}
                    >
                      <Select style={{ width: 200 }}>
                        {Object.entries(bookingStatusLabels).map(
                          ([value, label]) => (
                            <Select.Option key={value} value={value}>
                              {label}
                            </Select.Option>
                          )
                        )}
                      </Select>
                    </Form.Item>
                  ) : (
                    <Tag color={getStatusColor(booking.status)}>
                      {booking.status
                        ? bookingStatusLabels[
                            booking.status as keyof typeof bookingStatusLabels
                          ] || booking.status
                        : "Unknown"}
                    </Tag>
                  )}
                </p>
                <p>
                  <strong>Created:</strong> {formatDateTime(booking.created_at)}
                </p>
                <p>
                  <strong>Company:</strong>{" "}
                  {(() => {
                    const companyName = booking.company_info
                      ? Array.isArray(booking.company_info)
                        ? booking.company_info[0]?.name
                        : booking.company_info?.name
                      : "Unknown";

                    return (
                      <span>
                        {companyName}{" "}
                        {booking.company_id && (
                          <Tooltip title="View Company Details">
                            <Button
                              type="link"
                              icon={<ExportOutlined />}
                              onClick={handleViewCompany}
                              style={{ padding: "0 4px" }}
                            />
                          </Tooltip>
                        )}
                      </span>
                    );
                  })()}
                </p>
                {booking.company_id && (
                  <div style={{ marginTop: 8, marginBottom: 8 }}>
                    <strong>Associated Users:</strong>
                    {loadingCompanyUsers ? (
                      <div style={{ marginTop: 4 }}>Loading users...</div>
                    ) : companyUsers.length > 0 ? (
                      <div style={{ marginTop: 4 }}>
                        {companyUsers
                          .filter(
                            (user) =>
                              user.email &&
                              !user.is_admin &&
                              !user.email.endsWith("@efreightforwarding.com") // Filter out efreightforwarding emails
                          )
                          .map((user) => (
                            <div
                              key={user.id}
                              style={{
                                marginBottom: 4,
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <span
                                style={{
                                  fontFamily: "monospace",
                                  background: "#f5f5f5",
                                  padding: "2px 6px",
                                  borderRadius: "4px",
                                  userSelect: "all",
                                  cursor: "text",
                                }}
                                title="Click to select email"
                              >
                                {user.email}
                              </span>
                              <Button
                                type="text"
                                size="small"
                                icon={<CopyOutlined />}
                                onClick={() => {
                                  navigator.clipboard.writeText(user.email!);
                                  message.success(
                                    `Copied ${user.email} to clipboard`
                                  );
                                }}
                                title="Copy email to clipboard"
                                style={{ padding: "0 4px", minWidth: "auto" }}
                              />
                            </div>
                          ))}
                        {companyUsers.filter(
                          (user) =>
                            user.email &&
                            !user.is_admin &&
                            !user.email.endsWith("@efreightforwarding.com")
                        ).length === 0 && (
                          <div style={{ marginTop: 4, color: "#666" }}>
                            No non-admin users with external emails found
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ marginTop: 4, color: "#666" }}>
                        No users associated with this company
                      </div>
                    )}
                  </div>
                )}
              </Col>
              <Col span={12}>
                <p>
                  <strong>Transport Type:</strong>{" "}
                  {editMode ? (
                    <Form.Item
                      name="transport_type"
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: "Please select a transport type",
                        },
                      ]}
                    >
                      <Select
                        style={{ width: 200 }}
                        loading={loadingTransportTypes}
                        placeholder="Select transport type"
                      >
                        {transportTypes.map((type) => (
                          <Select.Option key={type.id} value={type.id}>
                            {type.abbreviation === "AIR"
                              ? `✈️ ${type.name}`
                              : type.abbreviation === "SEA"
                              ? `🚢 ${type.name}`
                              : type.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    (() => {
                      let transportName = "N/A";
                      let transportAbbr = "";

                      if (booking.transport_type_info) {
                        const transportInfo = Array.isArray(
                          booking.transport_type_info
                        )
                          ? booking.transport_type_info[0]
                          : booking.transport_type_info;

                        transportName = transportInfo?.name || "N/A";
                        transportAbbr = transportInfo?.abbreviation || "";
                      }

                      if (transportAbbr === "AIR") {
                        return `✈️ ${transportName}`;
                      } else if (transportAbbr === "SEA") {
                        return `🚢 ${transportName}`;
                      }

                      return transportName;
                    })()
                  )}
                </p>
                <p>
                  <strong>Booking Type:</strong>{" "}
                  {editMode ? (
                    <Form.Item name="booking_type" style={{ marginBottom: 0 }}>
                      <Select
                        style={{ width: 200 }}
                        loading={loadingBookingTypes}
                        placeholder="Select booking type"
                        allowClear
                      >
                        {bookingTypes.map((type) => (
                          <Select.Option key={type.id} value={type.id}>
                            {type.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    (() => {
                      if (booking.booking_type_info) {
                        const bookingTypeInfo = Array.isArray(
                          booking.booking_type_info
                        )
                          ? booking.booking_type_info[0]
                          : booking.booking_type_info;
                        return bookingTypeInfo?.name || "N/A";
                      }
                      return "N/A";
                    })()
                  )}
                </p>
                <p>
                  <strong>Incoterm:</strong>{" "}
                  {editMode ? (
                    <Form.Item
                      name="booking_incoterm"
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: "Please select an incoterm",
                        },
                      ]}
                    >
                      <Select
                        style={{ width: 200 }}
                        loading={loadingIncoterms}
                        placeholder="Select incoterm"
                      >
                        {incoterms.map((incoterm) => (
                          <Select.Option key={incoterm.id} value={incoterm.id}>
                            {incoterm.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                  ) : (
                    incoterm?.name || "N/A"
                  )}
                </p>
                {booking.container_id && (
                  <p>
                    <strong>Container Reference:</strong>{" "}
                    {container?.reference || "N/A"}{" "}
                    <Tag color="blue">Consolidated</Tag>
                  </p>
                )}
              </Col>
            </Row>

            <Divider />

            <Row gutter={[16, 16]}>
              <Col span={12}>
                <p>
                  <strong>Company:</strong> {company?.name || "N/A"}
                </p>
                <p>
                  <strong>Origin Pickup Date:</strong>{" "}
                  {editMode ? (
                    <Form.Item
                      name="origin_pickup_date"
                      style={{ marginBottom: 0 }}
                      rules={[
                        {
                          required: true,
                          message: "Please select a pickup date",
                        },
                      ]}
                    >
                      <DatePicker style={{ width: 200 }} />
                    </Form.Item>
                  ) : (
                    formatDate(booking.origin_pickup_date)
                  )}
                </p>
              </Col>
              <Col span={12}>
                <div>
                  <strong>Agent:</strong>{" "}
                  {editMode ? (
                    <Select
                      loading={loadingAgents}
                      style={{ width: "100%", marginTop: 6 }}
                      placeholder="Select an agent"
                      value={selectedAgentId}
                      onChange={handleAgentChange}
                      allowClear
                    >
                      {agents.map((agent) => (
                        <Select.Option key={agent.id} value={agent.id}>
                          {agent.name} {agent.email ? `(${agent.email})` : ""}
                        </Select.Option>
                      ))}
                    </Select>
                  ) : (
                    <span style={{ marginLeft: 8 }}>
                      {agent?.name || "None"}
                      {agent?.email ? ` (${agent.email})` : ""}
                      {!agent && booking.status === BookingStatus.SUBMITTED && (
                        <Tag color="warning" style={{ marginLeft: 8 }}>
                          Required for approval
                        </Tag>
                      )}
                      <Button
                        size="small"
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => setEditMode(true)}
                        style={{ marginLeft: 8 }}
                        loading={updatingAgent}
                      >
                        Change
                      </Button>
                    </span>
                  )}
                </div>
                <p>
                  <strong>Confirmed Pickup Date:</strong>{" "}
                  {formatDate(booking.confirmed_pick_up_date)}
                </p>
              </Col>
            </Row>

            <Divider />
            <Form.Item
              label={<strong>Notes:</strong>}
              name="notes"
              rules={[
                {
                  max: 500,
                  message: "Notes cannot exceed 500 characters",
                },
              ]}
            >
              {editMode ? (
                <TextArea
                  placeholder="Enter booking notes"
                  rows={4}
                  maxLength={500}
                  showCount
                />
              ) : (
                <div>{booking.notes || "-"}</div>
              )}
            </Form.Item>

            {booking.rejection_reason && (
              <>
                <Divider />
                <p>
                  <strong>Rejection Reason:</strong>
                </p>
                <p style={{ color: "#f5222d" }}>{booking.rejection_reason}</p>
              </>
            )}

            <Divider />
            <div>
              <strong>Picqer Integration:</strong>{" "}
              {(() => {
                const companyInfo = booking.company_info
                  ? Array.isArray(booking.company_info)
                    ? booking.company_info[0]
                    : booking.company_info
                  : null;

                const isConnected =
                  companyInfo?.picqer_enabled && companyInfo?.picqer_api_key;
                const hasFullIntegration =
                  isConnected && companyInfo?.picqer_fulfilment_customer_id;

                if (hasFullIntegration) {
                  return (
                    <span>
                      <Tag color="green">Connected</Tag>
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: "12px",
                          color: "#666",
                        }}
                      >
                        Full integration with fulfillment customer configured
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          window.open(
                            `/admin/companies?id=${booking.company_id}`,
                            "_blank"
                          );
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        Open Picqer Settings
                      </Button>
                    </span>
                  );
                } else if (isConnected) {
                  return (
                    <span>
                      <Tag color="orange">Partially Connected</Tag>
                      <span
                        style={{
                          marginLeft: 8,
                          fontSize: "12px",
                          color: "#666",
                        }}
                      >
                        API key configured but no fulfillment customer selected
                      </span>
                      <Button
                        type="link"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          window.open(
                            `/admin/companies?id=${booking.company_id}`,
                            "_blank"
                          );
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        Open Picqer Settings
                      </Button>
                    </span>
                  );
                } else {
                  return (
                    <span>
                      <Tag color="red">Not Connected</Tag>
                      <Button
                        type="link"
                        size="small"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          window.open(
                            `/admin/companies?id=${booking.company_id}`,
                            "_blank"
                          );
                        }}
                        style={{ marginLeft: 8 }}
                      >
                        Configure in Company Settings
                      </Button>
                    </span>
                  );
                }
              })()}
            </div>
          </Form>
        </TabPane>

        <TabPane tab="Supplier Information" key="2">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEditSupplier}
              disabled={isEditingSupplier}
            >
              Edit
            </Button>
          </div>
          {isEditingSupplier ? (
            <Form
              form={supplierForm}
              layout="vertical"
              onValuesChange={handleSupplierFormChange}
            >
              <Card title="Supplier Information">
                <Row gutter={16}>
                  <Col span={12}>
                    <Form.Item
                      name="name"
                      label="Supplier Name"
                      rules={[
                        {
                          required: true,
                          message: "Please enter supplier name",
                        },
                      ]}
                    >
                      <Input placeholder="Enter supplier name" />
                    </Form.Item>

                    <Form.Item
                      name="email"
                      label="Email"
                      rules={[
                        {
                          type: "email",
                          message: "Please enter a valid email",
                        },
                      ]}
                    >
                      <Input placeholder="Enter email address" />
                    </Form.Item>

                    <Form.Item name="phone" label="Phone">
                      <Input placeholder="Enter phone number" />
                    </Form.Item>
                  </Col>

                  <Col span={12}>
                    <Form.Item name="address" label="Address">
                      <Input.TextArea placeholder="Enter address" rows={3} />
                    </Form.Item>

                    <Form.Item name="country" label="Country">
                      <Input placeholder="Enter country" />
                    </Form.Item>

                    <Form.Item name="province" label="Province">
                      <Input placeholder="Enter province" />
                    </Form.Item>
                  </Col>
                </Row>
              </Card>

              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button
                  onClick={handleCancelSupplierEdit}
                  style={{ marginRight: 8 }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveSupplier}
                  loading={savingSupplier}
                >
                  Save
                </Button>
              </div>
            </Form>
          ) : (
            <Card title="Supplier Information">
              {supplier ? (
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <p>
                      <strong>Supplier Name:</strong> {supplier.name}
                    </p>
                    <p>
                      <strong>Email:</strong> {supplier.email || "N/A"}
                    </p>
                    <p>
                      <strong>Phone:</strong> {supplier.phone || "N/A"}
                    </p>
                  </Col>
                  <Col span={12}>
                    <p>
                      <strong>Address:</strong> {supplier.address || "N/A"}
                    </p>
                    <p>
                      <strong>Country:</strong> {supplier.country || "N/A"}
                    </p>
                    <p>
                      <strong>Province:</strong> {supplier.province || "N/A"}
                    </p>
                  </Col>
                </Row>
              ) : (
                <p>No supplier information available.</p>
              )}
            </Card>
          )}
        </TabPane>

        <TabPane tab="Products" key="3">
          {products && products.length > 0 ? (
            <>
              <div
                style={{
                  marginBottom: 16,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <div>
                  <Typography.Title level={5}>
                    Total Products: {products.length}
                  </Typography.Title>
                </div>
                <Space>
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() => setShowProductImport(true)}
                  >
                    Import Products
                  </Button>
                  <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={() => onDownloadPackingSlip(booking)}
                  >
                    Generate & Download Packing List
                  </Button>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => {
                      // Get supplier info
                      const supplier: Supplier | undefined =
                        booking.supplier_info &&
                        Array.isArray(booking.supplier_info)
                          ? booking.supplier_info[0]
                          : booking.supplier_info;

                      // Ensure products is always a flat Product[]
                      let flatProducts: import("../../types/booking").Product[] =
                        [];
                      if (Array.isArray(products)) {
                        if (products.length > 0 && Array.isArray(products[0])) {
                          flatProducts = (
                            products as import("../../types/booking").Product[][]
                          )[0];
                        } else {
                          flatProducts =
                            products as import("../../types/booking").Product[];
                        }
                      }

                      // Keep original Dutch columns and add new requested fields
                      const excelColumns = [
                        { header: "Productcode", key: "ean" },
                        { header: "Naam", key: "name" },
                        { header: "HS code", key: "hs_code" },
                        { header: "Leverancier", key: "supplier_name" },
                        { header: "Barcode", key: "ean" },
                        {
                          header: "Gewicht per stuk (kg)",
                          key: "weight_per_piece",
                        },
                        { header: "Totaal Gewicht (kg)", key: "gross_weight" },
                        { header: "HS code", key: "hs_code" },
                        { header: "Pieces", key: "pieces" },
                        { header: "Cartons", key: "cartons" },
                        { header: "CBM", key: "cbm" },
                        { header: "Prijs", key: "value" },
                      ];

                      // Map products to rows with correct data
                      const rows = flatProducts.map((product) => ({
                        name: product.name || "",
                        ean: product.ean || "",
                        hs_code: product.hs_code || "",
                        pieces: product.pieces || 0,
                        cartons: product.cartons || 0,
                        gross_weight: product.gross_weight || 0,
                        weight_per_piece:
                          product.pieces && product.pieces > 0
                            ? Math.round(
                                (product.gross_weight / product.pieces) * 1000
                              ) / 1000
                            : 0,
                        cbm: product.cbm || 0,
                        value: product.value || 0,
                        supplier_name: supplier?.name || "",
                      }));

                      // Prepare worksheet data
                      const worksheetData = [
                        excelColumns.map((col) => col.header),
                        ...rows.map((row) =>
                          excelColumns.map(
                            (col) => row[col.key as keyof typeof row] || ""
                          )
                        ),
                      ];

                      const worksheet = XLSX.utils.aoa_to_sheet(worksheetData);
                      const workbook = XLSX.utils.book_new();
                      XLSX.utils.book_append_sheet(
                        workbook,
                        worksheet,
                        "Products"
                      );
                      XLSX.writeFile(
                        workbook,
                        `booking_${booking.id}_products.xlsx`
                      );
                    }}
                  >
                    Export Products to Excel
                  </Button>
                </Space>
              </div>
              <Table
                columns={productColumns}
                dataSource={products}
                rowKey="id"
                pagination={false}
                size="small"
                scroll={{ x: 1000 }}
              />
            </>
          ) : (
            <p>No product information available.</p>
          )}
        </TabPane>

        <TabPane tab="Delivery Information" key="4">
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              marginBottom: 16,
            }}
          >
            <Button
              type="primary"
              icon={<EditOutlined />}
              onClick={handleEditDelivery}
              disabled={isEditingDelivery}
            >
              Edit
            </Button>
          </div>
          {isEditingDelivery ? (
            <Form
              form={deliveryForm}
              layout="vertical"
              onValuesChange={handleDeliveryFormChange}
            >
              <Card title="Delivery Type" style={{ marginBottom: 16 }}>
                <Radio.Group
                  value={deliveryType}
                  onChange={(e) => setDeliveryType(e.target.value)}
                >
                  <Radio value="address">Deliver to Address</Radio>
                  <Radio value="storage">Store in Warehouse</Radio>
                </Radio.Group>

                {deliveryType === "storage" && (
                  <Alert
                    style={{ marginTop: 16 }}
                    type="info"
                    message="Warehouse Storage"
                    description="The goods will be stored in the E-Freight warehouse in The Netherlands until further notice."
                  />
                )}
              </Card>

              {deliveryType === "address" && (
                <Card title="Delivery Address">
                  <Row gutter={16}>
                    <Col span={16}>
                      <Form.Item
                        name="street_name"
                        label="Street Name"
                        rules={[
                          {
                            required: true,
                            message: "Please enter street name",
                          },
                        ]}
                      >
                        <Input placeholder="Enter street name" />
                      </Form.Item>
                    </Col>
                    <Col span={8}>
                      <Form.Item
                        name="house_number"
                        label="House Number"
                        rules={[
                          {
                            required: true,
                            message: "Please enter house number",
                          },
                        ]}
                      >
                        <Input placeholder="Enter house number" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item
                        name="city"
                        label="City"
                        rules={[
                          { required: true, message: "Please enter city" },
                        ]}
                      >
                        <Input placeholder="Enter city" />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="postal_code"
                        label="Postal Code"
                        rules={[
                          {
                            required: true,
                            message: "Please enter postal code",
                          },
                        ]}
                      >
                        <Input placeholder="Enter postal code" />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Form.Item
                    name="country"
                    label="Country"
                    rules={[
                      { required: true, message: "Please enter country" },
                    ]}
                  >
                    <Input placeholder="Enter country" />
                  </Form.Item>

                  <Form.Item name="instructions" label="Special Instructions">
                    <Input.TextArea
                      placeholder="Enter any special delivery instructions"
                      rows={3}
                    />
                  </Form.Item>
                </Card>
              )}

              <div style={{ marginTop: 16, textAlign: "right" }}>
                <Button
                  onClick={handleCancelDeliveryEdit}
                  style={{ marginRight: 8 }}
                >
                  Cancel
                </Button>
                <Button
                  type="primary"
                  onClick={handleSaveDelivery}
                  loading={processingDelivery}
                >
                  Save
                </Button>
              </div>
            </Form>
          ) : (
            <>
              <Row gutter={[16, 16]}>
                {deliveryInfo ? (
                  <Col span={16}>
                    <Card title="Delivery Address">
                      <Row gutter={[16, 16]}>
                        <Col span={12}>
                          <Descriptions bordered column={1}>
                            <Descriptions.Item label="Street">
                              {deliveryInfo.street_name || "N/A"}{" "}
                              {deliveryInfo.house_number || ""}
                            </Descriptions.Item>
                            <Descriptions.Item label="City">
                              {deliveryInfo.city || "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Postal Code">
                              {deliveryInfo.postal_code || "N/A"}
                            </Descriptions.Item>
                            <Descriptions.Item label="Country">
                              {deliveryInfo.country || "N/A"}
                            </Descriptions.Item>
                          </Descriptions>
                        </Col>
                        <Col span={12}>
                          {deliveryInfo.instructions && (
                            <Card title="Special Instructions" size="small">
                              <p>{deliveryInfo.instructions}</p>
                            </Card>
                          )}
                        </Col>
                      </Row>
                    </Card>
                  </Col>
                ) : (
                  <Col span={16}>
                    <Card title="Warehouse Storage">
                      <Alert
                        type="info"
                        message="Warehouse Storage"
                        description="This booking's goods will be stored in the E-Freight warehouse in The Netherlands until further notice."
                        showIcon
                      />
                    </Card>
                  </Col>
                )}

                {/* Products Overview */}
                <Col span={8}>
                  <Card
                    title={`Products Overview (${products?.length || 0})`}
                    size="small"
                  >
                    {products && products.length > 0 ? (
                      <div style={{ maxHeight: "300px", overflowY: "auto" }}>
                        {products.map((product, index) => (
                          <div
                            key={product.id || index}
                            style={{
                              padding: "8px 0",
                              borderBottom:
                                index < products.length - 1
                                  ? "1px solid #f0f0f0"
                                  : "none",
                            }}
                          >
                            <div
                              style={{
                                fontSize: "13px",
                                fontWeight: 500,
                                color: "#262626",
                                marginBottom: "4px",
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                              }}
                            >
                              {product.name || `Product ${index + 1}`}
                            </div>
                            <div
                              style={{
                                fontSize: "11px",
                                color: "#8c8c8c",
                                marginBottom: "4px",
                              }}
                            >
                              {product.gross_weight
                                ? `${product.gross_weight}kg`
                                : "No weight"}{" "}
                              | {product.pieces || 0} pcs
                            </div>
                            <div>
                              <Tag
                                color="blue"
                                style={{ fontSize: "10px", padding: "1px 6px" }}
                              >
                                📦 {product.cartons || 0} est.
                              </Tag>
                              {/* Show actual cartons if available */}
                              {product.actual_cartons !== null &&
                                product.actual_cartons !== undefined && (
                                  <Tag
                                    color={
                                      product.actual_cartons === product.cartons
                                        ? "green"
                                        : "orange"
                                    }
                                    style={{
                                      fontSize: "10px",
                                      padding: "1px 6px",
                                      marginLeft: "4px",
                                    }}
                                  >
                                    ✅ {product.actual_cartons} actual
                                  </Tag>
                                )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div
                        style={{
                          textAlign: "center",
                          color: "#8c8c8c",
                          padding: "20px 0",
                        }}
                      >
                        No products available
                      </div>
                    )}

                    {products && products.length > 0 && (
                      <div
                        style={{
                          marginTop: "12px",
                          paddingTop: "12px",
                          borderTop: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <div
                          style={{
                            fontSize: "12px",
                            color: "#595959",
                            marginBottom: "4px",
                          }}
                        >
                          Total Cartons
                        </div>
                        <Space direction="vertical" size="small">
                          <Tag
                            color="blue"
                            style={{ fontSize: "12px", padding: "2px 8px" }}
                          >
                            📦{" "}
                            {products.reduce(
                              (sum, product) => sum + (product.cartons || 0),
                              0
                            )}{" "}
                            estimated
                          </Tag>
                          {/* Show actual total if any products have actual counts or booking has total_actual_cartons */}
                          {booking.total_actual_cartons !== null &&
                          booking.total_actual_cartons !== undefined ? (
                            <Tag
                              color="green"
                              style={{ fontSize: "12px", padding: "2px 8px" }}
                            >
                              ✅ {booking.total_actual_cartons} actual (for
                              shipments)
                            </Tag>
                          ) : (
                            products.some(
                              (p) =>
                                p.actual_cartons !== null &&
                                p.actual_cartons !== undefined
                            ) && (
                              <Tag
                                color="orange"
                                style={{ fontSize: "12px", padding: "2px 8px" }}
                              >
                                🔄{" "}
                                {products.reduce(
                                  (sum, product) =>
                                    sum +
                                    (product.actual_cartons ||
                                      product.cartons ||
                                      0),
                                  0
                                )}{" "}
                                partial count
                              </Tag>
                            )
                          )}
                        </Space>
                      </div>
                    )}
                  </Card>
                </Col>
              </Row>

              {/* Shipping Options - only show for delivery addresses */}
              {deliveryInfo && (
                <>
                  <div style={{ marginTop: 24 }}>
                    <ShippingOptions
                      deliveryInfo={deliveryInfo}
                      bookingId={booking.id}
                      deliveryCartons={
                        deliveryInfo.delivery_cartons ||
                        booking.total_actual_cartons ||
                        undefined
                      }
                      deliveryPallets={
                        deliveryInfo.delivery_pallets ||
                        booking.total_actual_pallets ||
                        undefined
                      }
                      onCreateShipment={(shippingMethodId) => {
                        console.log(
                          "Create shipment with method:",
                          shippingMethodId
                        );
                      }}
                      onShipmentCreated={() => {
                        // Force refresh of the SendcloudShipments component
                        setRefreshKey((prev) => prev + 1);
                        // Mark booking as completed when shipment is created
                        markBookingAsCompleted();
                      }}
                      disabled={isEditingDelivery}
                    />
                  </div>

                  {/* Sendcloud Shipments Display */}
                  <SendcloudShipments
                    key={refreshKey}
                    bookingId={booking.id}
                    onRefresh={() => {
                      // Optional: could trigger a refresh of booking data
                      console.log("Shipments refreshed");
                    }}
                  />
                </>
              )}
            </>
          )}
        </TabPane>

        <TabPane tab="Documents" key="5">
          <Collapse
            defaultActiveKey={
              otherDocuments.length === 0 ? ["other-documents"] : []
            }
          >
            <Collapse.Panel
              key="commercial-invoice"
              header={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    Commercial Invoice
                  </Typography.Title>
                  {booking.commercial_invoice_file && (
                    <Tag color="success">
                      {commercialInvoiceFiles.length} file(s) uploaded
                    </Tag>
                  )}
                </div>
              }
              extra={
                booking.commercial_invoice_file && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <FileOutlined style={{ marginRight: 8 }} />
                    {commercialInvoiceFiles.length === 1 ? (
                      <>
                        <span style={{ marginRight: 8 }}>
                          {commercialInvoiceFiles[0].split("/").pop() ||
                            "Commercial Invoice"}
                        </span>
                        <Space>
                          <Button
                            size="small"
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              handleDownloadCommercialInvoice(
                                commercialInvoiceFiles[0]
                              )
                            }
                            title="Download"
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() =>
                              handleViewCommercialInvoice(
                                commercialInvoiceFiles[0]
                              )
                            }
                            title="Preview"
                          />
                        </Space>
                      </>
                    ) : (
                      <span>
                        {commercialInvoiceFiles.length} invoice files available
                      </span>
                    )}
                  </div>
                )
              }
            >
              {booking.commercial_invoice_file ? (
                <div>
                  {commercialInvoiceFiles.length === 1 ? (
                    <Row
                      gutter={16}
                      align="middle"
                      style={{ marginBottom: 16 }}
                    >
                      <Col>
                        <FileOutlined
                          style={{ fontSize: "24px", marginRight: "8px" }}
                        />
                      </Col>
                      <Col flex="1">
                        <Text strong>
                          {commercialInvoiceFiles[0].split("/").pop() ||
                            "Commercial Invoice"}
                        </Text>
                      </Col>
                      <Col>
                        <Space>
                          <Button
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              handleDownloadCommercialInvoice(
                                commercialInvoiceFiles[0]
                              )
                            }
                          >
                            Download
                          </Button>
                          <Button
                            type="primary"
                            icon={<EyeOutlined />}
                            onClick={() =>
                              handleViewCommercialInvoice(
                                commercialInvoiceFiles[0]
                              )
                            }
                          >
                            Preview
                          </Button>
                        </Space>
                      </Col>
                    </Row>
                  ) : (
                    <div style={{ marginBottom: 16 }}>
                      <Dropdown
                        menu={{
                          items: commercialInvoiceFiles.map((file, idx) => ({
                            key: `invoice-${idx}`,
                            label: (
                              <Row gutter={16} align="middle">
                                <Col>
                                  <FileOutlined />
                                </Col>
                                <Col flex="1">
                                  {file.split("/").pop() ||
                                    `Invoice ${idx + 1}`}
                                </Col>
                                <Col>
                                  <Space>
                                    <Button
                                      size="small"
                                      icon={<DownloadOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleDownloadCommercialInvoice(
                                          file,
                                          idx
                                        );
                                      }}
                                    />
                                    <Button
                                      size="small"
                                      type="primary"
                                      icon={<EyeOutlined />}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleViewCommercialInvoice(file);
                                      }}
                                    />
                                  </Space>
                                </Col>
                              </Row>
                            ),
                          })),
                        }}
                        trigger={["click"]}
                      >
                        <Button style={{ marginBottom: 16 }}>
                          Commercial Invoices ({commercialInvoiceFiles.length}){" "}
                          <DownOutlined />
                        </Button>
                      </Dropdown>
                    </div>
                  )}

                  <Divider />

                  <div style={{ marginTop: "16px" }}>
                    <Title level={5}>Add Another Commercial Invoice</Title>
                    <Dragger {...uploadProps}>
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                      <p className="ant-upload-hint">
                        Support for PDF, Word, or Excel files up to 10MB
                      </p>
                    </Dragger>

                    <div style={{ marginTop: 8, marginBottom: 8 }}>
                      <Checkbox
                        checked={appendInvoice}
                        onChange={(e) => setAppendInvoice(e.target.checked)}
                      >
                        Add to existing invoices (uncheck to replace all)
                      </Checkbox>
                    </div>

                    <Button
                      type="primary"
                      onClick={handleCommercialInvoiceUpload}
                      disabled={!invoiceFile}
                      loading={uploadingInvoice}
                      style={{ marginTop: "8px" }}
                    >
                      Upload Additional Commercial Invoice
                    </Button>
                  </div>
                </div>
              ) : (
                <div>
                  <Text
                    type="secondary"
                    style={{ marginBottom: "16px", display: "block" }}
                  >
                    No commercial invoice has been uploaded for this booking.
                  </Text>

                  <div style={{ marginTop: "16px" }}>
                    <Dragger {...uploadProps}>
                      <p className="ant-upload-drag-icon">
                        <UploadOutlined />
                      </p>
                      <p className="ant-upload-text">
                        Click or drag file to this area to upload
                      </p>
                      <p className="ant-upload-hint">
                        Support for PDF, Word, or Excel files up to 10MB
                      </p>
                    </Dragger>

                    <Button
                      type="primary"
                      onClick={handleCommercialInvoiceUpload}
                      disabled={!invoiceFile}
                      loading={uploadingInvoice}
                      style={{ marginTop: "16px" }}
                    >
                      Upload Commercial Invoice
                    </Button>
                  </div>
                </div>
              )}
            </Collapse.Panel>

            <Collapse.Panel
              key="booking-invoices"
              header={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    Booking Invoices
                  </Typography.Title>
                  {bookingInvoices.length > 0 && (
                    <Tag color="success">
                      {bookingInvoices.length} file(s) uploaded
                    </Tag>
                  )}
                </div>
              }
              extra={
                bookingInvoices.length > 0 && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <FileOutlined style={{ marginRight: 8 }} />
                    {bookingInvoices.length === 1 ? (
                      <>
                        <span style={{ marginRight: 8 }}>
                          {bookingInvoices[0].booking_invoice_file
                            .split("/")
                            .pop() || "Booking Invoice"}
                        </span>
                        <Space>
                          <Button
                            size="small"
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              handleDownloadBookingInvoice(
                                bookingInvoices[0].booking_invoice_file,
                                bookingInvoices[0].id
                              )
                            }
                            title="Download"
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() =>
                              handleViewBookingInvoice(
                                bookingInvoices[0].booking_invoice_file
                              )
                            }
                            title="Preview"
                          />
                        </Space>
                      </>
                    ) : (
                      <span>
                        {bookingInvoices.length} invoice file(s) available
                      </span>
                    )}
                  </div>
                )
              }
            >
              {bookingInvoices.length > 0 ? (
                <div>
                  <Table
                    dataSource={bookingInvoices}
                    rowKey="id"
                    loading={loadingInvoices}
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: "File Name",
                        dataIndex: "booking_invoice_file",
                        key: "file_name",
                        render: (text) =>
                          text.split("/").pop() || "Booking Invoice",
                      },
                      {
                        title: "Date",
                        dataIndex: "created_at",
                        key: "created_at",
                        render: (text) => formatDate(text),
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, record) => (
                          <Space>
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={() =>
                                handleViewBookingInvoice(
                                  record.booking_invoice_file
                                )
                              }
                              title="Preview"
                            />
                            <Button
                              type="text"
                              icon={<DownloadOutlined />}
                              onClick={() =>
                                handleDownloadBookingInvoice(
                                  record.booking_invoice_file,
                                  record.id
                                )
                              }
                              title="Download"
                            />
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => setDeleteInvoiceId(record.id)}
                              title="Delete"
                            />
                          </Space>
                        ),
                      },
                    ]}
                  />

                  <Modal
                    title="Confirm Invoice Deletion"
                    open={!!deleteInvoiceId}
                    onCancel={() => setDeleteInvoiceId(null)}
                    onOk={handleDeleteBookingInvoice}
                    confirmLoading={deletingInvoice}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                  >
                    <p>
                      Are you sure you want to delete this invoice? This action
                      cannot be undone.
                    </p>
                  </Modal>

                  <Divider />

                  <div style={{ marginTop: "16px" }}>
                    <Title level={5}>Upload New Invoice</Title>
                    <Form layout="vertical">
                      <Form.Item>
                        <Dragger {...bookingInvoiceUploadProps}>
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for PDF, Word, or Excel files up to 10MB
                          </p>
                        </Dragger>

                        <Button
                          type="primary"
                          onClick={handleBookingInvoiceUpload}
                          disabled={!bookingInvoiceFile}
                          loading={uploadingBookingInvoice}
                          style={{ marginTop: "16px" }}
                        >
                          Upload Invoice
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>
                </div>
              ) : (
                <div>
                  <Text
                    type="secondary"
                    style={{ marginBottom: "16px", display: "block" }}
                  >
                    No booking invoices have been uploaded for this booking.
                  </Text>

                  <div style={{ marginTop: "16px" }}>
                    <Form layout="vertical">
                      <Form.Item>
                        <Dragger {...bookingInvoiceUploadProps}>
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for PDF, Word, or Excel files up to 10MB
                          </p>
                        </Dragger>

                        <Button
                          type="primary"
                          onClick={handleBookingInvoiceUpload}
                          disabled={!bookingInvoiceFile}
                          loading={uploadingBookingInvoice}
                          style={{ marginTop: "16px" }}
                        >
                          Upload Invoice
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>
                </div>
              )}
            </Collapse.Panel>

            <Collapse.Panel
              key="other-documents"
              header={
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    width: "100%",
                    alignItems: "center",
                  }}
                >
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    Other Documents
                  </Typography.Title>
                  {otherDocuments.length > 0 && (
                    <Tag color="success">
                      {otherDocuments.length} file(s) uploaded
                    </Tag>
                  )}
                </div>
              }
              extra={
                otherDocuments.length > 0 && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", alignItems: "center" }}
                  >
                    <FileOutlined style={{ marginRight: 8 }} />
                    {otherDocuments.length === 1 ? (
                      <>
                        <span style={{ marginRight: 8 }}>
                          {otherDocuments[0].booking_other_documents_file
                            .split("/")
                            .pop() || "Document"}
                        </span>
                        <Space>
                          <Button
                            size="small"
                            type="text"
                            icon={<DownloadOutlined />}
                            onClick={() =>
                              handleDownloadOtherDocument(
                                otherDocuments[0].booking_other_documents_file,
                                otherDocuments[0].id
                              )
                            }
                            title="Download"
                          />
                          <Button
                            size="small"
                            type="text"
                            icon={<EyeOutlined />}
                            onClick={() =>
                              handleViewOtherDocument(
                                otherDocuments[0].booking_other_documents_file
                              )
                            }
                            title="Preview"
                          />
                        </Space>
                      </>
                    ) : (
                      <span>{otherDocuments.length} document(s) available</span>
                    )}
                  </div>
                )
              }
            >
              {otherDocuments.length > 0 ? (
                <div>
                  <Table
                    dataSource={otherDocuments}
                    rowKey="id"
                    loading={loadingOtherDocuments}
                    pagination={false}
                    size="small"
                    columns={[
                      {
                        title: "File Name",
                        dataIndex: "booking_other_documents_file",
                        key: "file_name",
                        render: (text) => text.split("/").pop() || "Document",
                      },
                      {
                        title: "Date",
                        dataIndex: "created_at",
                        key: "created_at",
                        render: (text) => formatDate(text),
                      },
                      {
                        title: "Actions",
                        key: "actions",
                        render: (_, record) => (
                          <Space>
                            <Button
                              type="text"
                              icon={<EyeOutlined />}
                              onClick={() =>
                                handleViewOtherDocument(
                                  record.booking_other_documents_file
                                )
                              }
                              title="Preview"
                            />
                            <Button
                              type="text"
                              icon={<DownloadOutlined />}
                              onClick={() =>
                                handleDownloadOtherDocument(
                                  record.booking_other_documents_file,
                                  record.id
                                )
                              }
                              title="Download"
                            />
                            <Button
                              type="text"
                              danger
                              icon={<DeleteOutlined />}
                              onClick={() => setDeleteDocumentId(record.id)}
                              title="Delete"
                            />
                          </Space>
                        ),
                      },
                    ]}
                  />

                  <Modal
                    title="Confirm Document Deletion"
                    open={!!deleteDocumentId}
                    onCancel={() => setDeleteDocumentId(null)}
                    onOk={handleDeleteOtherDocument}
                    confirmLoading={deletingDocument}
                    okText="Delete"
                    okButtonProps={{ danger: true }}
                  >
                    <p>
                      Are you sure you want to delete this document? This action
                      cannot be undone.
                    </p>
                  </Modal>

                  <Divider />

                  <div style={{ marginTop: "16px" }}>
                    <Title level={5}>Upload New Document</Title>
                    <Form layout="vertical">
                      <Form.Item>
                        <Dragger {...otherDocumentUploadProps}>
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for PDF, Word, or Excel files up to 10MB
                          </p>
                        </Dragger>

                        <Button
                          type="primary"
                          onClick={handleOtherDocumentUpload}
                          disabled={!otherDocumentFile}
                          loading={uploadingOtherDocument}
                          style={{ marginTop: "16px" }}
                        >
                          Upload Document
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>
                </div>
              ) : (
                <div>
                  <Text
                    type="secondary"
                    style={{ marginBottom: "16px", display: "block" }}
                  >
                    No other documents have been uploaded for this booking.
                  </Text>

                  <div style={{ marginTop: "16px" }}>
                    <Form layout="vertical">
                      <Form.Item>
                        <Dragger {...otherDocumentUploadProps}>
                          <p className="ant-upload-drag-icon">
                            <UploadOutlined />
                          </p>
                          <p className="ant-upload-text">
                            Click or drag file to this area to upload
                          </p>
                          <p className="ant-upload-hint">
                            Support for PDF, Word, or Excel files up to 10MB
                          </p>
                        </Dragger>

                        <Button
                          type="primary"
                          onClick={handleOtherDocumentUpload}
                          disabled={!otherDocumentFile}
                          loading={uploadingOtherDocument}
                          style={{ marginTop: "16px" }}
                        >
                          Upload Document
                        </Button>
                      </Form.Item>
                    </Form>
                  </div>
                </div>
              )}
            </Collapse.Panel>
          </Collapse>
        </TabPane>

        <TabPane tab="Container" key="6">
          {booking.container_id && container ? (
            <>
              <Card title="Container Information" style={{ marginBottom: 16 }}>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Descriptions bordered column={1}>
                      <Descriptions.Item label="Container ID">
                        {container.id}
                      </Descriptions.Item>
                      <Descriptions.Item label="Reference">
                        {container.reference || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Estimated Departure">
                        {formatDate(container.etd)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Estimated Arrival">
                        {formatDate(container.eta)}
                      </Descriptions.Item>
                      <Descriptions.Item label="Transportation Mode">
                        {(() => {
                          let transportName = "N/A";
                          let transportAbbr = "";

                          if (
                            container.transport_type &&
                            booking.transport_type_info
                          ) {
                            const transportInfo = Array.isArray(
                              booking.transport_type_info
                            )
                              ? booking.transport_type_info[0]
                              : booking.transport_type_info;

                            if (transportInfo) {
                              transportName = transportInfo.name || "N/A";
                              transportAbbr = transportInfo.abbreviation || "";
                            }
                          }

                          if (transportAbbr === "AIR") {
                            return `✈️ ${transportName}`;
                          } else if (transportAbbr === "SEA") {
                            return `🚢 ${transportName}`;
                          }

                          return transportName;
                        })()}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                  <Col span={12}>
                    <Descriptions bordered column={1}>
                      <Descriptions.Item label="Status">
                        <Tag color={getStatusColor(container.status)}>
                          {container.status || "N/A"}
                        </Tag>
                      </Descriptions.Item>
                      <Descriptions.Item label="Track & Trace">
                        {container.track_and_trace || "N/A"}
                      </Descriptions.Item>
                      <Descriptions.Item label="Last Updated">
                        {formatDateTime(
                          container.updated_at || container.created_at
                        ) || "N/A"}
                      </Descriptions.Item>
                    </Descriptions>
                  </Col>
                </Row>

                {/* Display related bookings if available */}
                {container &&
                  Array.isArray(container.bookings) &&
                  container.bookings.length > 0 && (
                    <>
                      <Divider />
                      <Title level={5}>
                        Other Bookings in This Consolidation
                      </Title>
                      <Table
                        dataSource={
                          container.bookings?.filter(
                            (b) => b.id !== booking.id
                          ) || []
                        }
                        rowKey="id"
                        size="small"
                        pagination={false}
                        columns={[
                          {
                            title: "ID",
                            dataIndex: "id",
                            key: "id",
                          },
                          {
                            title: "Reference",
                            dataIndex: "reference",
                            key: "reference",
                            render: (text) => text || "-",
                          },
                          {
                            title: "Company",
                            dataIndex: "company_info",
                            key: "company",
                            render: (companyInfo) => {
                              if (
                                Array.isArray(companyInfo) &&
                                companyInfo.length > 0
                              ) {
                                return companyInfo[0].name;
                              } else if (companyInfo && companyInfo.name) {
                                return companyInfo.name;
                              }
                              return "N/A";
                            },
                          },
                          {
                            title: "Status",
                            dataIndex: "status",
                            key: "status",
                            render: (status) => (
                              <Tag color={getStatusColor(status)}>
                                {status
                                  ? bookingStatusLabels[
                                      status as keyof typeof bookingStatusLabels
                                    ] || status
                                  : "Unknown"}
                              </Tag>
                            ),
                          },
                        ]}
                      />
                    </>
                  )}
              </Card>

              {/* Container Unloading Status (Read-Only) */}
              <div style={{ marginTop: 16 }}>
                <Card
                  title={
                    <span style={{ color: "#722ed1", fontWeight: "bold" }}>
                      📦 Container Unloading Status
                    </span>
                  }
                  size="small"
                  style={{ backgroundColor: "#fafafa" }}
                >
                  <Alert
                    message="Read-Only View"
                    description="Unloading management is done from Admin → Containers → Container Details. This shows the current status only."
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />

                  <Descriptions bordered column={2} size="small">
                    <Descriptions.Item label="Unloading Status">
                      <Tag
                        color={
                          booking.unloading_status === "completed"
                            ? "green"
                            : booking.unloading_status === "in_progress"
                            ? "orange"
                            : "default"
                        }
                      >
                        {booking.unloading_status === "completed"
                          ? "✅ Completed"
                          : booking.unloading_status === "in_progress"
                          ? "🔄 In Progress"
                          : "⏳ Pending"}
                      </Tag>
                    </Descriptions.Item>

                    <Descriptions.Item label="Unloaded At">
                      {booking.unloaded_at
                        ? formatDate(booking.unloaded_at)
                        : "-"}
                    </Descriptions.Item>

                    <Descriptions.Item label="Actual Cartons">
                      <Text
                        strong
                        style={{
                          color: booking.total_actual_cartons
                            ? "#52c41a"
                            : "#999",
                        }}
                      >
                        {booking.total_actual_cartons || "-"}
                      </Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="Actual Pallets">
                      <Text
                        strong
                        style={{
                          color: booking.total_actual_pallets
                            ? "#52c41a"
                            : "#999",
                        }}
                      >
                        {booking.total_actual_pallets || "-"}
                      </Text>
                    </Descriptions.Item>

                    <Descriptions.Item label="Unloading Notes" span={2}>
                      {booking.unloading_notes || "-"}
                    </Descriptions.Item>
                  </Descriptions>

                  {booking.container_info && (
                    <div style={{ marginTop: 12 }}>
                      <Button
                        type="link"
                        icon={<LinkOutlined />}
                        onClick={() => {
                          const containerId = Array.isArray(
                            booking.container_info
                          )
                            ? booking.container_info[0]?.id
                            : booking.container_info?.id;
                          if (containerId) {
                            window.open(
                              `/admin/containers/${containerId}`,
                              "_blank"
                            );
                          }
                        }}
                      >
                        Manage Unloading in Container Details
                      </Button>
                    </div>
                  )}
                </Card>
              </div>
            </>
          ) : (
            <div>
              <Alert
                type="info"
                showIcon
                message="Not Linked to Container"
                description="This booking is not currently linked to any container."
                style={{ marginBottom: 16 }}
              />

              <Card title="Link to Container">
                <Alert
                  type="warning"
                  showIcon
                  message="Work in Progress"
                  description="Container linking functionality is currently under development."
                  style={{ marginBottom: 16 }}
                />
              </Card>
            </div>
          )}
        </TabPane>

        <TabPane tab="Invoices" key="7">
          <Card
            title="Sales Invoices"
            style={{ marginBottom: 16 }}
            extra={
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setCreateInvoiceModalVisible(true)}
              >
                Create Invoice
              </Button>
            }
          >
            {loadingSalesInvoices ? (
              <div style={{ textAlign: "center", padding: "20px" }}>
                Loading invoices...
              </div>
            ) : salesInvoices.length > 0 ? (
              <>
                {/* Invoice Statistics */}
                <Row gutter={16} style={{ marginBottom: 16 }}>
                  <Col span={8}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#1890ff",
                          }}
                        >
                          {salesInvoices.length}
                        </div>
                        <div style={{ color: "#666" }}>Total Invoices</div>
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color: "#3f8600",
                          }}
                        >
                          €
                          {salesInvoices
                            .reduce((total, invoice) => {
                              const invoiceTotal =
                                invoice.lines?.reduce(
                                  (lineTotal: number, line: any) => {
                                    return (
                                      lineTotal +
                                      line.quantity *
                                        line.unit_price *
                                        (1 + line.vat_percentage / 100)
                                    );
                                  },
                                  0
                                ) || 0;
                              return total + invoiceTotal;
                            }, 0)
                            .toFixed(2)}
                        </div>
                        <div style={{ color: "#666" }}>Total Revenue</div>
                      </div>
                    </Card>
                  </Col>
                  <Col span={8}>
                    <Card size="small">
                      <div style={{ textAlign: "center" }}>
                        <div
                          style={{
                            fontSize: "24px",
                            fontWeight: "bold",
                            color:
                              salesInvoices.filter(
                                (inv) => inv.moneybird_payment_status !== "paid"
                              ).length > 0
                                ? "#cf1322"
                                : "#3f8600",
                          }}
                        >
                          €
                          {salesInvoices
                            .reduce((total, invoice) => {
                              if (invoice.moneybird_payment_status === "paid")
                                return total;
                              const invoiceTotal =
                                invoice.lines?.reduce(
                                  (lineTotal: number, line: any) => {
                                    return (
                                      lineTotal +
                                      line.quantity *
                                        line.unit_price *
                                        (1 + line.vat_percentage / 100)
                                    );
                                  },
                                  0
                                ) || 0;
                              return total + invoiceTotal;
                            }, 0)
                            .toFixed(2)}
                        </div>
                        <div style={{ color: "#666" }}>Outstanding Balance</div>
                      </div>
                    </Card>
                  </Col>
                </Row>

                {/* Invoices Table */}
                <Table
                  dataSource={salesInvoices}
                  rowKey="id"
                  pagination={{ pageSize: 5, showSizeChanger: false }}
                  size="small"
                  columns={[
                    {
                      title: "Invoice Date",
                      dataIndex: "created_at",
                      key: "created_at",
                      render: (date) => formatDate(date),
                      width: 120,
                    },
                    {
                      title: "Booking Reference",
                      key: "booking_reference",
                      render: (record) => {
                        const transportType = record.booking
                          ?.transport_type_info
                          ? Array.isArray(record.booking.transport_type_info)
                            ? record.booking.transport_type_info[0]
                                ?.abbreviation || ""
                            : record.booking.transport_type_info.abbreviation ||
                              ""
                          : "";
                        return `EFF${record.booking_id}${transportType}`;
                      },
                      width: 150,
                    },
                    {
                      title: "Payment Status",
                      dataIndex: "moneybird_payment_status",
                      key: "payment_status",
                      render: (status) => {
                        const statusConfig = {
                          paid: { color: "#3f8600", text: "Paid" },
                          open: { color: "#fa8c16", text: "Open" },
                          overdue: { color: "#cf1322", text: "Overdue" },
                          partial: { color: "#722ed1", text: "Partial" },
                          cancelled: { color: "#666", text: "Cancelled" },
                        };
                        const config = statusConfig[
                          status as keyof typeof statusConfig
                        ] || { color: "#666", text: status || "Unknown" };
                        return <Tag color={config.color}>{config.text}</Tag>;
                      },
                      width: 120,
                    },
                    {
                      title: "Amount",
                      key: "amount",
                      render: (record) => {
                        const total =
                          record.lines?.reduce((sum: number, line: any) => {
                            return (
                              sum +
                              line.quantity *
                                line.unit_price *
                                (1 + line.vat_percentage / 100)
                            );
                          }, 0) || 0;
                        return (
                          <span style={{ fontWeight: "bold" }}>
                            €{total.toFixed(2)}
                          </span>
                        );
                      },
                      width: 100,
                      align: "right",
                    },
                    {
                      title: "Due Date",
                      dataIndex: "moneybird_due_date",
                      key: "due_date",
                      render: (date) => (date ? formatDate(date) : "-"),
                      width: 120,
                    },
                    {
                      title: "Moneybird ID",
                      dataIndex: "moneybird_invoice_invoice_id",
                      key: "moneybird_id",
                      render: (id) => id || "-",
                      width: 120,
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      render: (record) => {
                        const moneybirdId = record.moneybird_invoice_id;
                        if (!moneybirdId) return "-";

                        const administrationId = "443982818600552286"; // Correct Moneybird administration ID
                        const moneybirdUrl = `https://moneybird.com/${administrationId}/sales_invoices/${moneybirdId}`;
                        return (
                          <a
                            href={moneybirdUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{ color: "#1890ff" }}
                          >
                            Open in Moneybird
                          </a>
                        );
                      },
                      width: 150,
                    },
                  ]}
                />
              </>
            ) : (
              <div
                style={{ textAlign: "center", padding: "40px", color: "#666" }}
              >
                <div style={{ fontSize: "16px", marginBottom: "8px" }}>
                  No invoices found
                </div>
                <div>
                  No sales invoices have been created for this booking yet.
                </div>
              </div>
            )}
          </Card>
        </TabPane>

        <TabPane tab="Purchase Orders" key="8">
          <PurchaseOrderSection booking={booking} onRefresh={onRefresh} />
        </TabPane>

        <TabPane
          tab={
            <>
              Tasks
              {hasPendingTasks() && (
                <Badge
                  status="processing"
                  color="orange"
                  style={{ marginLeft: 8 }}
                />
              )}
            </>
          }
          key="10"
        >
          <div style={{ marginBottom: 24 }}>
            <Title level={5}>Tasks</Title>

            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleAddTask}
            >
              Add Task
            </Button>
          </div>

          <Table
            columns={taskColumns}
            dataSource={tasks}
            rowKey="id"
            loading={loadingTasks}
            pagination={false}
            size="small"
          />

          {/* Task Form Modal */}
          <Modal
            title={editingTask ? "Edit Task" : "Add Task"}
            open={isTaskModalVisible}
            onOk={handleSaveTask}
            onCancel={handleCancelTask}
            confirmLoading={processingTaskAction}
            width={600}
          >
            <Form form={taskForm} layout="vertical">
              <Form.Item
                name="company_id"
                label="Company"
                initialValue={booking.company_id}
                hidden
              >
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                name="booking_id"
                label="Booking"
                initialValue={booking.id}
                hidden
              >
                <Input type="hidden" />
              </Form.Item>

              <Form.Item
                name="name"
                label="Task Name"
                rules={[
                  { required: true, message: "Please enter a task name" },
                ]}
              >
                <Input placeholder="Enter task name" />
              </Form.Item>

              <Form.Item name="description" label="Description">
                <TextArea
                  placeholder="Enter task description"
                  rows={4}
                  showCount
                  maxLength={1000}
                />
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item name="date" label="Due Date">
                    <DatePicker
                      style={{ width: "100%" }}
                      format="YYYY-MM-DD"
                      placeholder="Select due date"
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item name="team_member" label="Assign To">
                    <Select placeholder="Select team member" allowClear>
                      {memberOptions.map((option) => (
                        <Select.Option key={option.value} value={option.value}>
                          {option.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="completed" valuePropName="checked">
                <Checkbox>Mark as completed</Checkbox>
              </Form.Item>
            </Form>
          </Modal>

          {/* Task Delete Confirmation Modal */}
          <Modal
            title="Delete Task"
            open={!!deletingTaskId}
            onOk={handleDeleteTask}
            onCancel={() => setDeletingTaskId(null)}
            confirmLoading={processingTaskAction}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <p>
              Are you sure you want to delete this task? This action cannot be
              undone.
            </p>
          </Modal>
        </TabPane>
      </Tabs>

      <div
        style={{
          marginTop: 16,
          display: "flex",
          justifyContent: "space-between",
        }}
      >
        {/* Approval/Rejection buttons (only for submitted bookings) */}
        {booking.status === BookingStatus.SUBMITTED && (
          <div>
            <Space>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={handleApproveBooking}
                loading={approvingBooking}
                style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
                disabled={!booking.agent_id && agents.length === 0}
                title={
                  !booking.agent_id && agents.length === 0
                    ? "An agent must be assigned before approving"
                    : ""
                }
              >
                Approve Booking
              </Button>
              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => setShowRejectModal(true)}
                loading={rejectingBooking}
              >
                Reject Booking
              </Button>
            </Space>
          </div>
        )}

        {/* Save/Cancel buttons for General tab (only show when editing) */}
        {editMode && (
          <div style={{ marginLeft: "auto" }}>
            <Space>
              <Button icon={<UndoOutlined />} onClick={handleCancel}>
                Cancel
              </Button>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                onClick={handleSave}
                loading={saving}
              >
                Save Changes
              </Button>
            </Space>
          </div>
        )}
      </div>

      {/* Approval Modal */}
      <Modal
        title="Approve Booking"
        open={showApprovalModal}
        onCancel={() => {
          setShowApprovalModal(false);
          setApprovalNotes("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowApprovalModal(false);
              setApprovalNotes("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="approve"
            type="primary"
            loading={approvingBooking}
            onClick={handleApproveBookingWithNotes}
            style={{ backgroundColor: "#52c41a", borderColor: "#52c41a" }}
          >
            Approve
          </Button>,
        ]}
      >
        <div>
          <p>Are you sure you want to approve this booking?</p>
          <Input.TextArea
            placeholder="Add approval notes (optional)"
            rows={4}
            value={approvalNotes}
            onChange={(e) => setApprovalNotes(e.target.value)}
            maxLength={500}
          />
        </div>
      </Modal>

      {/* Rejection Modal */}
      <Modal
        title="Reject Booking"
        open={showRejectModal}
        onCancel={() => {
          setShowRejectModal(false);
          setRejectionReason("");
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setShowRejectModal(false);
              setRejectionReason("");
            }}
          >
            Cancel
          </Button>,
          <Button
            key="reject"
            danger
            loading={rejectingBooking}
            onClick={handleRejectBooking}
          >
            Reject
          </Button>,
        ]}
      >
        <div>
          <p>
            Are you sure you want to reject this booking? This action cannot be
            undone.
          </p>
          <Input.TextArea
            placeholder="Reason for rejection (required)"
            rows={4}
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </div>
      </Modal>

      {/* Booking Invoice Preview Modal */}
      <Modal
        title="Booking Invoice Preview"
        open={bookingInvoicePreviewVisible}
        onCancel={() => setBookingInvoicePreviewVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setBookingInvoicePreviewVisible(false)}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {bookingInvoicePreviewUrl && (
          <div style={{ height: "600px", overflow: "auto" }}>
            <iframe
              src={bookingInvoicePreviewUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Booking Invoice Preview"
            />
          </div>
        )}
      </Modal>

      {/* Commercial Invoice Preview Modal */}
      <Modal
        title="Commercial Invoice Preview"
        open={invoicePreviewVisible}
        onCancel={() => setInvoicePreviewVisible(false)}
        footer={[
          <Button key="close" onClick={() => setInvoicePreviewVisible(false)}>
            Close
          </Button>,
          <Button
            key="download"
            type="primary"
            onClick={() => handleDownloadCommercialInvoice()}
          >
            Download
          </Button>,
        ]}
        width={800}
      >
        {invoicePreviewUrl && (
          <div style={{ height: "600px", overflow: "auto" }}>
            <iframe
              src={invoicePreviewUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Commercial Invoice Preview"
            />
          </div>
        )}
      </Modal>

      {/* Product Edit Modal */}
      <Modal
        title="Edit Product"
        open={isProductModalVisible}
        onCancel={() => {
          setIsProductModalVisible(false);
          setEditingProduct(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setIsProductModalVisible(false);
              setEditingProduct(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="save"
            type="primary"
            loading={savingProduct}
            onClick={handleSaveProduct}
          >
            Save
          </Button>,
        ]}
      >
        <Form form={productForm} layout="vertical">
          <Form.Item
            name="name"
            label="Product Name"
            rules={[{ required: true, message: "Please enter product name" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="hs_code" label="HS Code">
            <Input />
          </Form.Item>
          <Form.Item name="ean" label="EAN">
            <Input />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="pieces" label="Pieces">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cartons" label="Cartons">
                <InputNumber min={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="gross_weight" label="Gross Weight (kg)">
                <InputNumber
                  min={0}
                  step={0.01}
                  precision={2}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="cbm" label="CBM">
                <InputNumber
                  min={0}
                  step={0.001}
                  precision={3}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="value" label="Value">
            <InputNumber
              min={0}
              step={0.01}
              precision={2}
              style={{ width: "100%" }}
              prefix="€"
            />
          </Form.Item>
        </Form>
      </Modal>

      {/* Delete product confirmation modal */}
      <Modal
        title="Delete Product"
        open={deleteProductModalVisible}
        onCancel={() => {
          setDeleteProductModalVisible(false);
          setProductToDelete(null);
        }}
        footer={[
          <Button
            key="cancel"
            onClick={() => {
              setDeleteProductModalVisible(false);
              setProductToDelete(null);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="delete"
            type="primary"
            danger
            loading={deletingProduct}
            onClick={handleDeleteProduct}
          >
            Delete
          </Button>,
        ]}
        width={600}
      >
        <p>
          Are you sure you want to delete the product "{productToDelete?.name}"?
        </p>
        <p>This action cannot be undone.</p>
      </Modal>

      {/* Other Document Preview Modal */}
      <Modal
        title="Other Document Preview"
        open={otherDocumentPreviewVisible}
        onCancel={() => setOtherDocumentPreviewVisible(false)}
        footer={[
          <Button
            key="close"
            onClick={() => setOtherDocumentPreviewVisible(false)}
          >
            Close
          </Button>,
        ]}
        width={800}
      >
        {otherDocumentPreviewUrl && (
          <div style={{ height: "600px", overflow: "auto" }}>
            <iframe
              src={otherDocumentPreviewUrl}
              style={{ width: "100%", height: "100%", border: "none" }}
              title="Other Document Preview"
            />
          </div>
        )}
      </Modal>

      {/* Create Invoice Modal */}
      <Modal
        title="Create New Invoice"
        open={createInvoiceModalVisible}
        onCancel={() => setCreateInvoiceModalVisible(false)}
        footer={null}
        width={1200}
        style={{ top: 20 }}
      >
        <InvoiceForm
          onSuccess={handleCreateInvoiceSuccess}
          onCancel={() => setCreateInvoiceModalVisible(false)}
          initialBookingId={booking.id}
        />
      </Modal>

      {/* Product Import Modal */}
      <BookingProductImport
        visible={showProductImport}
        onCancel={() => setShowProductImport(false)}
        onSuccess={() => {
          setShowProductImport(false);
          onRefresh(); // Refresh booking data to show new products
        }}
        bookingId={booking.id}
        companyId={booking.company_id}
      />
    </>
  );
};

export default BookingDetails;
